import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { legacyAdminApi as adminApi, devicesApi, settingsWriteApi, templatesApi, followBackApi, phoneDriverTransitionsApi } from '../lib/api';
import PhoneDriverTransitionNotice from '../components/PhoneDriverTransitionNotice';
import { isSupersedable, isRouteLocked, transitionPollMs, refusalMessage, routeSaveNotice, cancelNotice, logTransition, viewFields } from '../lib/phoneDriverTransition';
import { useAuth } from '../contexts/AuthContext';
import { FIELD_METADATA, getFieldMeta, getFieldsBySection, getAllFieldsBySection, generateDefaultSettings, SECTION_ORDER, MAIN_CATEGORIES, flattenSettings, unflattenSettings, applyAutoSettings } from '../lib/fieldMetadataComplete';
import { r14UiEnabled, r16mUiEnabled, r16nUiEnabled, r16pUiEnabled } from '../lib/r14Switch';
import { dmOpenUnreadHint, DM_INBOX_IDS } from '../lib/dmInboxHint';
import { registerServerOnlyFields, coerceForForm } from '../lib/serverSettings';
import { PLATFORM_FIELD, slotPlatform, withPlatformLabel, platformName, readyTikTokModules } from '../lib/platform';
import { selectOptions, currentOptionValue } from '../lib/settingsOptions';
import { reserveFab, releaseFab } from '../lib/fabStack';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, ArrowUp, Save, Settings2, RefreshCw, Search, Info, LayoutGrid, Layers, List, ScrollText, ChevronRight, ChevronsDownUp, ChevronsUpDown, BookmarkPlus, BookOpen, Trash2, History, Undo2, ShieldOff, Stethoscope } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { fromTimeInput, toTimeInput } from '../lib/settingsTime';
import { diffSettings } from '../lib/settingsDiff';
import { blankSourceWarning, valuesById, fieldForId } from '../lib/blankSourceWarning';
import { batch1217Warning, batchValuesById, BATCH_IDS } from '../lib/batch1217Warning';
import { storyCfCheck, STORY_CF_IDS } from '../lib/storyCloseFriends';
import { normaliseBatches } from '../lib/followBackView';
import RouterLink from '../components/stats/RouterLink';
import { REPOST_CAPTION_FIELD, REPOST_CAPTION_TAG, describeTemplate } from '../lib/repostCaptionTemplate';
import RepostCaptionPreview from '../components/RepostCaptionPreview';
import useDoctorMe from '../hooks/useDoctorMe';
import { doctorUiOn, doctorSwitches, DOCTOR_TAG } from '../lib/doctorSwitch';
import {
  normPanel, saveFindings as pickSaveFindings, answerFindings, answerSaveFindings, notesNotShown, blockedToast, focusFromSearch, rulesLine,
} from '../lib/doctorView';
import SlotBanner from '../components/doctor/SlotBanner';
import DoctorDrawer, { usePanel, panelQueryKey } from '../components/doctor/DoctorDrawer';
import SaveFindingsCard from '../components/doctor/SaveFindingsCard';
import { readPanel } from '../lib/doctorCalls';
import useChatsOwner from '../hooks/useChatsOwner';

// ---------------------------------------------------------------------------------------------
// U4 data layer: values are keyed by SETTING ID on the wire (FIELD_METADATA[field].number). The
// form still works in field-name space; these two functions are the only bridge.
// ---------------------------------------------------------------------------------------------

// One value as the server writes it (updateValue takes strings; booleans as 'true'/'false').
const toWire = (v) => {
  if (v === true) return 'true';
  if (v === false) return 'false';
  if (v === null || v === undefined) return null;
  return String(v);
};

/*
 * Must stay in step with SettingsMetadataIndex.IDENTITY_FIELDS on the server - the server is the
 * one that actually refuses, this list only decides whether to ask first.
 *
 * deviceID is deliberately NOT here (2026-08-27): identity means "which account this slot is", and
 * a shared phone is a normal fleet layout - one device legitimately runs many accounts - not two
 * slots claiming the same Instagram account.
 *
 * r16 (2026-10-03): socialPlatform (setting 15 "Platform", Instagram / TikTok - lib/platform.js) is identity too: it
 * says which network the account is on. Without it here every save of the dropdown would be a 403 (the server asks
 * for allowIdentity); with it the customer confirms the switch first.
 */
const IDENTITY_FIELDS = ['slotLabel', 'userName', 'password', PLATFORM_FIELD];

// r16h (2026-10-12): one note of a save's `notes` (PUT /slots/{slot}/settings) - a sentence, or { message } / { text }.
const serverNoteText = (n) => (typeof n === 'string' ? n : (n && (n.message || n.text)) || '');

// The id of setting 723 "Repost Caption Template" as the server's metadata registered it (null on a server without it).
const repostCaptionId = () => {
  const m = FIELD_METADATA[REPOST_CAPTION_FIELD];
  return m && m.number !== undefined && m.number !== null ? String(m.number) : null;
};

const VIEW_MODES = {
  ALL: 'all',
  ACCORDION: 'accordion',
  TABS: 'tabs',
  NESTED: 'nested'
};

/*
 * The chosen view is remembered per browser. The three original views keep living in
 * 'slotSettingsViewMode' exactly as before; "All" is remembered in a key of its own ON TOP of it.
 *
 * Why not just store 'all' in the same key: the build before this one draws NOTHING for a view
 * name it does not know (each view is `viewMode === X && ...`). Rolling this change back would
 * then leave every customer who had picked All on a settings page with no settings on it. With the
 * separate key, a rollback simply shows them the view they used before All.
 *
 * Reads are validated for the same reason in the other direction: an unknown saved value falls
 * back to Accordion - still the default for anyone with no saved choice - instead of a blank page.
 */
const VIEW_MODE_KEY = 'slotSettingsViewMode';
const ALL_VIEW_KEY = 'slotSettingsAllView';
const CLASSIC_VIEW_MODES = [VIEW_MODES.ACCORDION, VIEW_MODES.TABS, VIEW_MODES.NESTED];

function readSavedViewMode() {
  try {
    if (localStorage.getItem(ALL_VIEW_KEY) === '1') return VIEW_MODES.ALL;
    const saved = localStorage.getItem(VIEW_MODE_KEY);
    return CLASSIC_VIEW_MODES.includes(saved) ? saved : VIEW_MODES.ACCORDION;
  } catch (e) {
    // storage blocked (private mode, site data off): the choice is simply not remembered
    console.warn('[slot-settings] saved view unreadable, using Accordion:', e && e.message);
    return VIEW_MODES.ACCORDION;
  }
}

function saveViewMode(mode) {
  try {
    if (mode === VIEW_MODES.ALL) {
      localStorage.setItem(ALL_VIEW_KEY, '1');
    } else {
      localStorage.removeItem(ALL_VIEW_KEY);
      localStorage.setItem(VIEW_MODE_KEY, mode);
    }
  } catch (e) {
    // storage blocked: the view still switches, it just is not remembered
    console.warn('[slot-settings] view choice not saved:', e && e.message);
  }
}

/*
 * Accordion view, owner 2026-09-29: "make [all modules expanded] the default thing, and that
 * checkmark thing optional ... for the way how it's currently now". So every section starts OPEN
 * (several at once, each header still closes its own), and the "One section at a time" checkbox
 * brings back the old behaviour exactly - one open section, opening another closes it.
 *
 * Remembered per browser under a key of its own: '1' = one at a time, '0' or nothing = all open.
 * Nothing else reads the key, so a rollback (git revert) simply ignores it - the older build is
 * one-at-a-time for everyone again, with no stale value to trip over.
 */
const ACCORDION_SINGLE_KEY = 'slotSettingsAccordionSingle';

function readAccordionSingle() {
  try {
    return localStorage.getItem(ACCORDION_SINGLE_KEY) === '1';
  } catch (e) {
    // storage blocked: the choice is not remembered, and the page opens with every section open
    console.warn('[slot-settings] accordion choice unreadable, opening every section:', e && e.message);
    return false;
  }
}

function saveAccordionSingle(single) {
  try {
    localStorage.setItem(ACCORDION_SINGLE_KEY, single ? '1' : '0');
  } catch (e) {
    // storage blocked: the accordion still switches, it just is not remembered
    console.warn('[slot-settings] accordion choice not saved:', e && e.message);
  }
}

/*
 * Accordion view with every section open: where the section a jump or the scroll-follow lands on
 * counts as "the one you are in". A section is current once its top has passed this many pixels
 * from the top of the window - below the shell's top bar (3.75rem) and, on phones, the sticky
 * section chips under it. The least it can be: with the phone chips showing, the scroll-follow
 * moves it down to just below where they pin (see spyLine - the top bar is taller in an iPhone
 * home-screen app).
 */
const SPY_LINE_PX = 140;

// Scroll a sidebar list / chip strip (never the window) just enough to show one of its items.
function keepItemInView(list, item, horizontal) {
  if (!list || !item) return;
  if (!(horizontal ? list.clientWidth : list.clientHeight)) return;   // hidden at this breakpoint
  const l = list.getBoundingClientRect();
  const r = item.getBoundingClientRect();
  const pad = 12;
  if (horizontal) {
    if (r.left < l.left) list.scrollLeft -= (l.left - r.left) + pad;
    else if (r.right > l.right) list.scrollLeft += (r.right - l.right) + pad;
  } else if (r.top < l.top) {
    list.scrollTop -= (l.top - r.top) + pad;
  } else if (r.bottom > l.bottom) {
    list.scrollTop += (r.bottom - l.bottom) + pad;
  }
}

// The same slug the accordion view builds its per-section test ids from.
const sectionSlug = (sectionName) => sectionName.replace(/\s+/g, '-').toLowerCase();

// A Min field's Max partner - the one renderField draws on the Min's row. null for any other field.
const maxPartnerOf = (fieldName) => {
  if (fieldName.endsWith('Min')) return fieldName.slice(0, -3) + 'Max';
  if (fieldName.endsWith('min')) return fieldName.slice(0, -3) + 'max';
  return null;
};

/*
 * All view and Accordion view: a section's controls re-render only when something they are drawn
 * from changed.
 *
 * Measured 2026-09-28 on a production build in Chrome, 631 controls on the page (every plan gate
 * open): without this, ONE keystroke re-rendered all of them - 27 ms median on the desktop and
 * 238 ms with the CPU throttled 4x (a mid-range phone), i.e. visibly laggy typing. The Accordion
 * view opens every section by default since 2026-09-29, so it draws the same ~630 controls and
 * uses it too. Tabs and Nested draw one section at a time and do not need it.
 *
 * `inputs` is everything renderField reads apart from the module-level FIELD_METADATA (built by
 * sectionFieldInputs inside the page). `draw` is only called when the inputs differ, so it always
 * runs with the current render's closures; handleInputChange uses functional updates, so a control
 * kept from an earlier render still writes correctly.
 */
const sameInputs = (a, b) => a.length === b.length && a.every((x, i) => Object.is(x, b[i]));
const MemoSectionFields = React.memo(
  function MemoSectionFields({ draw }) { return draw(); },
  (prev, next) => sameInputs(prev.inputs, next.inputs),
);

/*
 * One Accordion-view section - its header and, through MemoSectionFields, its controls - skipped
 * on a render in which nothing it is drawn from changed. Open/closed is NOT a prop: Radix reads it
 * from the Accordion's own context, so opening or closing redraws just what Radix owns.
 *
 * Measured 2026-09-29 on a production build in Chrome, every section open (633 controls), CPU
 * throttled 4x: with MemoSectionFields alone a keystroke still re-rendered all 17 Radix items and
 * took 69-94 ms (median) against 46 ms for the All view. With this: 41-48 ms (All view 20-45 ms,
 * one section at a time 30-32 ms - the rest is the page's own per-keystroke work). Unthrottled,
 * all three are ~5 ms.
 */
const AccordionSection = React.memo(
  function AccordionSection({ sectionName, count, anchorOffset, registerRef, inputs, draw }) {
    const slug = sectionSlug(sectionName);
    return (
      <AccordionItem
        value={sectionName}
        ref={(el) => registerRef(sectionName, el)}
        className={`aurora-card overflow-hidden ${anchorOffset}`}
        data-testid={`accordion-section-${slug}`}
      >
        <AccordionTrigger className="px-4 py-3 hover:bg-secondary/40 hover:no-underline" data-testid={`accordion-trigger-${slug}`}>
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 pr-2">
            <Settings2 className="h-5 w-5 shrink-0 text-primary" />
            <span className="font-heading text-base font-semibold text-foreground">{sectionName}</span>
            <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full tnum">
              {count} settings
            </span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <MemoSectionFields inputs={inputs} draw={draw} />
        </AccordionContent>
      </AccordionItem>
    );
  },
  (prev, next) => prev.sectionName === next.sectionName
    && prev.count === next.count
    && prev.anchorOffset === next.anchorOffset
    && prev.registerRef === next.registerRef
    && sameInputs(prev.inputs, next.inputs),
);

// First load (and the moment before the form state exists): the page's shape in Aurora's pulse -
// header, the view/search card, a few field rows - instead of a lone spinner.
function SettingsSkeleton() {
  return (
    <div className="space-y-5 sm:space-y-6" aria-busy="true" aria-label="Loading slot settings" data-testid="slot-settings-skeleton">
      <div className="space-y-2">
        <div className="h-4 w-16 animate-pulse rounded-xl bg-secondary/60" />
        <div className="h-8 w-56 max-w-full animate-pulse rounded-xl bg-secondary/60" />
        <div className="h-3 w-40 animate-pulse rounded-xl bg-secondary/60" />
      </div>
      <div className="h-24 animate-pulse rounded-xl bg-secondary/60" />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-secondary/60" />)}
      </div>
      <div className="h-40 animate-pulse rounded-xl bg-secondary/60" />
    </div>
  );
}

export default function SlotSettingsPage() {
  const { slotName } = useParams();
  const { user: me } = useAuth();   // plan flags for V1's th:hidden gates
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  usePageTitle('Slot settings');
  const [settings, setSettings] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  /*
   * The floating Save button and the Undead assistant bubble both want the bottom-right corner.
   * Save is contextual and urgent, so it keeps the corner and the bubble lifts above it; the
   * height reserved here is the lg button (2.75rem) plus a 1rem gap. Released when the edits are
   * saved and on unmount, so the bubble drops back down.
   */
  useEffect(() => {
    if (hasChanges) reserveFab('slot-settings-save', 60);
    else releaseFab('slot-settings-save');
  }, [hasChanges]);
  useEffect(() => () => releaseFab('slot-settings-save'), []);
  // r16sd: a Settings Doctor link opens this page on one setting (?focus=<id>): the search box starts as "#<id>"
  const [searchQuery, setSearchQuery] = useState(() => {
    try {
      const f = doctorSwitches().master ? focusFromSearch(window.location.search) : null;
      return f ? `#${f}` : '';
    } catch (e) {
      return '';
    }
  });
  const [viewMode, setViewMode] = useState(readSavedViewMode);
  const [activeTab, setActiveTab] = useState(null);
  const [activeCategory, setActiveCategory] = useState(Object.keys(MAIN_CATEGORIES)[0]);
  const [activeSubTab, setActiveSubTab] = useState(null);
  
  // For accordion view - track which section is open and active in sidebar
  const [openAccordionSection, setOpenAccordionSection] = useState(null);
  const [activeSidebarSection, setActiveSidebarSection] = useState(null);
  const sectionRefs = useRef({});

  /*
   * Accordion view, all-open (the default - see ACCORDION_SINGLE_KEY). The sections the user CLOSED
   * are what is kept, not the open ones, so every section is open unless someone closed it -
   * including sections that only appear later (the server's metadata arriving, a search changing
   * the list). openAccordionSection above is the one-at-a-time mode's state, untouched.
   */
  const [accordionSingle, setAccordionSingle] = useState(readAccordionSingle);
  const [closedSections, setClosedSections] = useState(() => new Set());
  const visibleSectionsRef = useRef([]);          // the accordion's sections as last drawn
  const spyLockRef = useRef({ until: 0, cap: 0 }); // scroll-follow paused until `until` (ms)
  const sidebarListRef = useRef(null);
  const sidebarItemRefs = useRef({});
  const chipStripRef = useRef(null);
  const chipRefs = useRef({});

  // All view: each section's card, and the jump strip its "Sections" buttons return to.
  const allSectionRefs = useRef({});
  const allJumpStripRef = useRef(null);

  // Template ("preset") dialogs state - the markup kept V2's names; the data is /uiapi/templates.
  const [savePresetDialogOpen, setSavePresetDialogOpen] = useState(false);
  const [loadPresetDialogOpen, setLoadPresetDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presetDescription, setPresetDescription] = useState('');
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  // The server's values keyed by FIELD NAME as loaded (form-space) - what "changed" is diffed
  // against on save, so only edited settings ever travel.
  const [baseline, setBaseline] = useState(null);
  const [requestedTransition, setRequestedTransition] = useState(null);

  // The random-persona block came from V2's FastAPI profile; no equivalent exists on this server.
  const enableRandomPersona = false;

  const { data: slotData, isLoading } = useQuery({
    queryKey: ['slotSettings', slotName],
    queryFn: () => adminApi.getSlotSettings(slotName).then(res => res.data),
  });

  const transitionId = requestedTransition?.operationId || slotData?.phoneDriverTransition?.operationId;
  const { data: transitionData } = useQuery({
    queryKey: ['phoneDriverTransition', transitionId],
    queryFn: () => phoneDriverTransitionsApi.get(transitionId).then(res => res.data),
    enabled: !!transitionId,
    refetchInterval: (query) => transitionPollMs(query.state.data),
  });
  const phoneTransition = transitionData || requestedTransition || slotData?.phoneDriverTransition;
  // Recovery contract (lib/phoneDriverTransition.js): a RUNNING job locks #128 on its requested route; a FAILED /
  // EXPIRED one does not - saving #128 again (either value) supersedes it with a new verified job.
  const routeLocked = isRouteLocked(phoneTransition);
  const routeSupersedable = isSupersedable(phoneTransition);
  // A deliberate #128 edit while the job is FAILED / EXPIRED: Save sends #128 even when it equals the baseline (a
  // re-save of the current route is the VERIFY job). Cleared by every save.
  const routeTouchedRef = useRef(false);
  // One route action (retry / cancel / save #128 again) at a time. A ref, not isPending: react-query publishes
  // isPending a tick later, and a double click inside that tick must still send ONE request.
  const routeInFlightRef = useRef(false);
  const runRouteAction = useCallback((name, run) => {
    if (routeInFlightRef.current) { logTransition('route-action-ignored', { detail: `${name} while another request is pending` }); return; }
    routeInFlightRef.current = true;
    run();
  }, []);
  const routeActionSettled = () => { routeInFlightRef.current = false; };
  // a #128 edit counts only for the FAILED / EXPIRED job it was made under
  useEffect(() => { if (!routeSupersedable) routeTouchedRef.current = false; }, [routeSupersedable, phoneTransition?.operationId]);
  useEffect(() => {
    if (typeof phoneTransition?.effectiveShared !== 'boolean') return;
    // Update only #128 as the async route commits; unrelated unsaved module edits stay intact.
    setBaseline(prev => prev ? { ...prev, useSharedPhoneDriver: phoneTransition.effectiveShared } : prev);
    setSettings(prev => prev ? { ...prev, useSharedPhoneDriver: phoneTransition.effectiveShared } : prev);
  }, [phoneTransition?.operationId, phoneTransition?.effectiveShared]);
  // A job view from retry / cancel / a #128 save becomes the page's job at once (also for the SAME operation id, whose
  // cached poll would otherwise still show the old phase).
  const adoptTransition = useCallback((view, event) => {
    if (!view || !view.operationId) return;
    queryClient.setQueryData(['phoneDriverTransition', view.operationId], view);
    setRequestedTransition(view);
    logTransition(event, viewFields(view, { detail: view.previousOperationId ? `previousOp=${view.previousOperationId}` : null }));
  }, [queryClient]);
  const retryTransition = useMutation({
    mutationFn: (job) => {
      logTransition('retry-requested', viewFields(job));
      return phoneDriverTransitionsApi.retry(job.operationId);
    },
    onSuccess: (res) => {
      adoptTransition(res.data, 'retry-accepted');
      toast.info('Phone driver switch requested again. The desktop verifies the phone before any slot restarts.');
    },
    onError: (error) => toast.error(refusalMessage(error, 'The phone is not ready for a safe retry', 'retry-refused')),
    onSettled: routeActionSettled,
  });
  const cancelTransition = useMutation({
    mutationFn: (job) => {
      logTransition('cancel-requested', viewFields(job));
      return phoneDriverTransitionsApi.cancel(job.operationId);
    },
    onSuccess: (res) => {
      adoptTransition(res.data, 'cancel-accepted');
      toast.info(cancelNotice(res.data));   // review round 2: names the stopped slots to start again
    },
    onError: (error) => toast.error(refusalMessage(error, 'The switch could not be cancelled', 'cancel-refused')),
    onSettled: routeActionSettled,
  });
  // "Save #128 again" from the notice (FAILED / EXPIRED): ONLY #128 travels, through the ordinary settings write, so the
  // server's supersede rule decides - and the page's other unsaved edits stay untouched (no reload of the form).
  const routeSave = useMutation({
    mutationFn: (shared) => {
      logTransition('route-save-requested', viewFields(phoneTransition, { detail: `#128=${shared}` }));
      return settingsWriteApi.put(slotName, { 128: shared ? 'true' : 'false' }, { reason: 'phone route recovery' });
    },
    onSuccess: (res) => {
      // react-query v5: the filter form - a bare array would invalidate (and reload) every query, the form included
      queryClient.invalidateQueries({ queryKey: ['settingsHistory', slotName] });
      routeTouchedRef.current = false;
      const job = res.data?.phoneDriverTransition;
      if (job) {
        adoptTransition(job, 'route-save-accepted');
        toast.info(routeSaveNotice(job));
      } else {
        logTransition('route-save-no-change', viewFields(phoneTransition));
        toast.info('Nothing to change: no switch is waiting on this phone.');
      }
    },
    onError: (error) => toast.error(refusalMessage(error, error.response?.data?.error || 'Failed to save setting #128', 'route-save-refused')),
    onSettled: routeActionSettled,
  });
  const routeActionPending = retryTransition.isPending || cancelTransition.isPending || routeSave.isPending;

  // r16 (2026-10-03): the slot's platform as the server reports it (INSTAGRAM for a server before r16) - for the
  // per-platform labels and the TikTok note. The hiding itself is the server's gates (hiddenByGate below).
  const platform = useMemo(() => slotPlatform(slotData), [slotData]);
  // r16 TT-3: the server's per-platform wording (platformLabels: a TikTok slot's story-like budget = the comment limits)
  const serverLabels = slotData && slotData.platformLabels;
  const metaFor = useCallback((fieldName) => withPlatformLabel(getFieldMeta(fieldName), fieldName, platform, serverLabels),
    [platform, serverLabels]);

  // Is the write path enabled? (super-admin kill switch: UIAPI_SETTINGS_WRITE)
  //
  // The ERROR matters as much as the answer. When this endpoint is not on the server at all - a
  // build without the settings-write package - the query fails, `writeInfo` stays undefined, and
  // `writesEnabled` is false. That is the right conclusion, but "the admin switched it off" is the
  // WRONG explanation for it, so the two cases are told apart below and the page says which.
  const { data: writeInfo, error: writeInfoError } = useQuery({
    queryKey: ['settingsWriteInfo'],
    queryFn: () => settingsWriteApi.info().then(res => res.data),
  });
  const writesEnabled = writeInfo?.enabled === true;
  const writesUnavailable = !!writeInfoError;
  const writesOffReason = writesUnavailable
    ? `This server has no settings-write endpoint (${writeInfoError.response?.status || 'unreachable'}), so nothing can be saved from this page.`
    : 'Saving is switched off by the administrator.';

  // Templates, aliased into the shape the (kept) V2 dialog markup reads.
  const { data: presets, refetch: refetchPresets } = useQuery({
    queryKey: ['settingsTemplates'],
    queryFn: () => templatesApi.list().then(res =>
      (res.data.templates || []).map(t => ({ ...t, created_at: t.createdAt }))),
    enabled: writesEnabled,
  });

  // Undo history for this slot (fetched when the dialog opens; refreshed after every write).
  const { data: historyData, refetch: refetchHistory } = useQuery({
    queryKey: ['settingsHistory', slotName],
    queryFn: () => settingsWriteApi.history(slotName).then(res => res.data),
    enabled: writesEnabled && historyDialogOpen,
  });

  /*
   * r16sd Settings Doctor (lib/doctorSwitch.js parts settingsHeader / drawer / saveFindings / blockedToast): this slot's holds as
   * banners under the header, "Check this slot", the findings of a save with their fixes, and the settings a save did not write
   * (blocked[], SD36). One panel read per page view (GET /uiapi/slots/{slot}/doctor, recomputed by the server), refreshed after each
   * save. A server without the doctor (no `doctor` on /uiapi/me): nothing is requested or drawn.
   */
  const doctor = useDoctorMe();
  const DOC_HEADER = doctor.available && doctorUiOn('settingsHeader');
  const DOC_DRAWER = doctor.available && doctorUiOn('drawer');
  const DOC_SAVE = doctor.available && doctorUiOn('saveFindings');
  const DOC_BLOCKED = doctorUiOn('blockedToast');
  // One read when the page opens, then one explicit refresh per save or fix: the page's own invalidateQueries([...]) calls (an
  // array, which react-query v5 does not take as a key filter) refetch every active query on each save, and a panel recompute must
  // not ride on them.
  // Review SF11: the boot read is per SLOT, not per mount - back / forward between two settings URLs keeps this page mounted, and
  // the new slot's panel is read once too (from the cache when it is fresh).
  const [panelBootedFor, setPanelBootedFor] = useState(null);
  const panelQ = usePanel(slotName, (DOC_HEADER || DOC_SAVE || DOC_DRAWER) && panelBootedFor !== slotName);
  const doctorPanel = panelQ.data ? normPanel(panelQ.data) : null;
  useEffect(() => {
    if (panelQ.isSuccess || panelQ.isError) setPanelBootedFor(slotName);
  }, [panelQ.isSuccess, panelQ.isError, slotName]);
  const doctorOwner = useChatsOwner();
  const doctorDeviceId = baseline && baseline.deviceID ? String(baseline.deviceID) : null;
  const refreshPanel = useCallback(
    () => queryClient.fetchQuery({ queryKey: panelQueryKey(doctorOwner, slotName), queryFn: () => readPanel(slotName).then((r) => r.data), staleTime: 0 }),
    [queryClient, slotName, doctorOwner],
  );
  const [doctorOpen, setDoctorOpen] = useState(false);
  const [saveCheck, setSaveCheck] = useState(null); // the findings of the last save: { findings, at }
  useEffect(() => { setSaveCheck(null); }, [slotName]); // another slot's save findings never show on this one (review SF11)
  const focusSetting = useCallback((id) => {
    if (id === null || id === undefined) return;
    console.info(`${DOCTOR_TAG} ${slotName}: setting ${id} brought into view`);
    setSearchQuery(`#${id}`);
    setClosedSections((prev) => (prev.size ? new Set() : prev));
    const el = typeof document !== 'undefined' ? document.querySelector('[data-testid="search-settings"]') : null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [slotName]);
  /**
   * After a save: the findings it touched - the save answer's own (stage 5, shown as sent: review SF3), else the freshly recomputed
   * panel's (only those whose settings the save touched). `u1` = the R16H U1 warning this save held back for the check (review SF5):
   * it is toasted here unless the check actually showed SD28 - never twice, never zero times.
   */
  const runSaveCheck = (data, sentIds, u1) => {
    const sayU1 = (why) => {
      if (!u1) return;
      (toast.warning || toast.info)(u1.title, { description: u1.text, duration: 15000 });
      console.info(`${u1.logLine} (save of ${slotName}; ${why})`);
    };
    if (!DOC_SAVE) { sayU1('the save check is off'); return; }
    const own = answerFindings(data);
    const show = (picked, origin) => {
        console.info(`${DOCTOR_TAG} save ${slotName}: ${picked.length} finding(s) to show (${origin}): ${rulesLine(picked)}`);
      setSaveCheck(picked.length ? { findings: picked, at: Date.now() } : null);
      if (picked.length) {
        (toast.warning || toast.info)(picked[0].text, {
          description: picked.length > 1 ? `${picked.length - 1} more at the top of this page` : undefined,
          duration: 12000,
        });
      }
      if (u1 && picked.some((f) => f.rule === 'SD28')) console.info(`${u1.logLine} (save of ${slotName}; the save check says it, no toast)`);
      else sayU1('the save check did not say SD28');
    };
    if (own) {
      show(answerSaveFindings(own), 'the save answer');
      refreshPanel().catch(() => {});
      return;
    }
    refreshPanel()
      .then((d) => show(pickSaveFindings(normPanel(d).findings, sentIds), 'the panel'))
      .catch((e) => {
            console.info(`${DOCTOR_TAG} save ${slotName}: the check could not be read (${e && e.response ? `HTTP ${e.response.status}` : 'no answer'})`);
        sayU1('the save check could not be read');
      });
  };

  // Fetch devices for the Select Shared Device dropdown
  const { data: devicesData } = useQuery({
    queryKey: ['devices'],
    queryFn: () => devicesApi.list().then(res => res.data),
  });

  /*
   * Round 14 (lib/r14Switch, default ON): V1's metadata is read BEFORE the values are mapped, so
   * settings the server defines and this bundle does not - the welcome DM per-hour/per-day limits,
   * "Pick targets in order (one per session)", "auto-remove not-found targets from my list" - are
   * added to FIELD_METADATA from it (lib/serverSettings.js) and load, render and save like any
   * other. The load effect waits for the metadata to settle (loaded OR failed; one retry). OFF:
   * the same query as round 13, no waiting, nothing added.
   */
  const R14 = r14UiEnabled();
  const { data: serverMeta, isFetched: serverMetaFetched } = useQuery({
    queryKey: ['settingsMetadata'],
    queryFn: () => settingsWriteApi.metadata().then(res => res.data),
    staleTime: Infinity,
    ...(R14 ? { retry: 1 } : {}),
  });
  const r14MetaReady = !R14 || serverMetaFetched;

  React.useEffect(() => {
    if (!slotData) return;
    if (!r14MetaReady) return;
    if (R14 && serverMeta) registerServerOnlyFields(serverMeta, FIELD_METADATA);
    // The Java server answers { found, readable, values: { "<settingId>": value } } - the same
    // getValue(id) accessor production's bulk feature uses. Map ids back to field names through
    // FIELD_METADATA[field].number; fields the server does not know keep their defaults and are
    // NEVER sent back.
    const defaultSettings = generateDefaultSettings();
    if (slotData.found === false || slotData.readable === false) {
      toast.error(slotData.error ? `This slot's settings cannot be read: ${slotData.error}` : 'Slot not found');
      setSettings(defaultSettings);
      setBaseline(null);
      return;
    }
    const values = slotData.values || {};
    const flat = {};
    Object.entries(FIELD_METADATA).forEach(([field, meta]) => {
      if (meta.number === undefined || meta.number === null) return;
      const raw = values[String(meta.number)];
      if (raw === undefined) return;
      flat[field] = coerceForForm(raw, defaultSettings[field], meta);	// UI-9: meta tells a declared switch from a name guess
    });
    if (typeof phoneTransition?.effectiveShared === 'boolean') flat.useSharedPhoneDriver = phoneTransition.effectiveShared;
    setSettings({ ...defaultSettings, ...flat });
    setBaseline(flat);
    setHasChanges(false);
    // serverMeta is deliberately not a dependency: re-seeding when it arrives would throw away
    // edits. r14MetaReady flips once, together with it (and is constant true with round 14 OFF).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotData, r14MetaReady]);

  /*
   * A "normalize deviceID to UUID" effect used to live here and made #7 unsaveable: pick device8,
   * press Save, and it came back device1.
   *
   * It re-ran on every change to settings.deviceID - including the user's own - and moved BOTH
   * settings and baseline to the selected value. handleSave only sends what differs from the
   * baseline, so the edit and the baseline became identical and deviceID was never put on the wire.
   * The save succeeded, wrote nothing, and the reload showed the stored value.
   *
   * The conversion it performed was a no-op anyway: /uiapi/devices returns `id` = the deviceN
   * string (UiApiController.deviceMap puts d.getDeviceID()), so device.id === device_id and there
   * is no UUID to convert to. That was a V2-era server shape. The effect's ONLY observable
   * behaviour was the bug, so it is gone rather than patched.
   *
   * Nothing needs to replace it: the Select's option values and settings.deviceID are both the
   * deviceN string, so they already match.
   */

  // Save view mode preference
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    saveViewMode(mode);
  };

  const updateMutation = useMutation({
    mutationFn: (payload) => settingsWriteApi.put(slotName, payload.values, payload.opts),
    onSuccess: (res, payload) => {
      queryClient.invalidateQueries(['slotSettings', slotName]);
      queryClient.invalidateQueries(['slots']);
      queryClient.invalidateQueries(['settingsHistory', slotName]);
      queryClient.invalidateQueries(['settingsWriteInfo']);
      setHasChanges(false);
      routeTouchedRef.current = false;
      const n = res.data?.changed ?? 0;
      if (res.data?.phoneDriverTransition) {
        adoptTransition(res.data.phoneDriverTransition, 'settings-save-job');
        toast.info(routeSaveNotice(res.data.phoneDriverTransition));
      } else toast.success(n === 0 ? 'Nothing had actually changed' : `${n} setting${n === 1 ? '' : 's'} saved (undo available in History)`);
      // r16h: the server's notes on a saved value (first: 723 - an unknown token or an unbalanced brace is saved and
      // posted as typed, and the customer is told so). Absent on every other save: nothing new is shown.
      const notes = (Array.isArray(res.data?.notes) ? res.data.notes : []).map(serverNoteText).filter(Boolean);
      // r16sd review MF1: a stage-5 answer repeats each finding's text in notes[]; the save check below says those (one toast + the
      // card), so they are not toasted a second time here. With the save check off every note is toasted, as on r16pd.
      const ownFindings = DOC_SAVE ? answerFindings(res.data) : null;
      const toastNotes = ownFindings ? notesNotShown(notes, answerSaveFindings(ownFindings)) : notes;
      if (toastNotes.length < notes.length) {
        console.info(`${DOCTOR_TAG} save ${slotName}: ${notes.length - toastNotes.length} server note(s) are the save check's own findings (${rulesLine(answerSaveFindings(ownFindings))}), said once`);
      }
      toastNotes.forEach((text) => toast.warning(text));
      // r16sd SD36: the ids the server refused (blocked[]) were never written - say which and why, never only "N saved"
      if (DOC_BLOCKED) {
        const refused = blockedToast(res.data && res.data.blocked, res.data);
        if (refused) {
          (toast.warning || toast.info)(refused.text, { duration: 15000, description: refused.description || undefined });
                console.info(`${DOCTOR_TAG} save ${slotName}: ${refused.ids.length} setting(s) not written: ${refused.ids.join(',')} (${refused.source})`);
        }
      }
      runSaveCheck(res.data, payload && payload.values ? Object.keys(payload.values) : [], payload && payload.u1);
      const rid = repostCaptionId();
      if (rid && payload && payload.values && payload.values[rid] !== undefined) {
        console.info(`${REPOST_CAPTION_TAG} V3 save ${slotName}: #${rid} saved (changed ${n}); server notes: ${notes.length ? notes.join(' | ') : 'none'}`);
      }
    },
    onError: (error, payload) => {
      const rid = repostCaptionId();
      if (rid && payload && payload.values && payload.values[rid] !== undefined) {
        console.info(`${REPOST_CAPTION_TAG} V3 save ${slotName}: #${rid} NOT saved - ${error.response?.status || 'no answer'}: `
          + `${error.response?.data?.error || error.message || 'unknown error'} (the text stays in the box)`);
      }
      // A 405 here is not a settings problem - it is this build having no PUT handler for the
      // route at all, and "Method Not Allowed" relayed verbatim tells the customer nothing.
      const status = error.response?.status;
      if (status === 404 || status === 405) {
        toast.error('This server cannot save settings', {
          description: `PUT ${error.response?.config?.url || '/slots/../settings'} answered ${status} - the settings-write endpoint is not deployed on it.`,
        });
        return;
      }
      // a phone-route refusal (e.g. 409 PHONE_DEVICE_IDS_DISAGREE) in plain words; any other message as the server wrote it
      toast.error(refusalMessage(error, error.response?.data?.error || 'Failed to save settings', 'settings-save-refused', { logUnknown: false }));
    },
  });

  /*
   * Revert (from History) and Apply Template both change server state and then invalidate
   * ['slotSettings', slotName]. The refetch re-runs the load effect, which rebuilds `settings` and
   * `baseline` from the response - correct, because the server now holds something different and
   * the form must show it.
   *
   * The problem is WHEN: both are reachable from a dialog while the form holds unsaved edits, and
   * the re-seed discards every one of them. This form has 600+ controls, so that can be a lot of
   * work. Both actions already confirmed, but neither confirm mentioned the edits - the user was
   * answering a question about 3 settings while 20 others were about to vanish.
   *
   * The saveMutation also re-seeds and is deliberately NOT routed through here: it re-seeds AFTER
   * persisting those same edits, so there is nothing to lose.
   */
  const confirmDiscardingEdits = (question) => {
    const warning = hasChanges
      ? '\n\nYou have unsaved changes on this page. They will be DISCARDED, because this reloads '
        + 'every setting from the server.'
      : '';
    return window.confirm(question + warning);
  };

  const handleSave = () => {
    if (!settings || !baseline) return;
    // Last gate before the request. Both save buttons are disabled without it, but a doomed PUT
    // must be impossible from ANY path, not just from the two that currently exist.
    if (!writesEnabled) { toast.error(writesOffReason); return; }
    // Only the settings that actually changed travel - diffed in wire form against what the
    // server sent, so an untouched form saves nothing and the undo history stays meaningful.
    const { changed, changedIdentity } =
      diffSettings(applyAutoSettings(settings), baseline, FIELD_METADATA, IDENTITY_FIELDS);
    // Recovery contract (d): with a FAILED / EXPIRED job, a deliberate #128 edit is a route request even when it ends on
    // the current route (that is the VERIFY job) - so #128 travels although it equals the baseline.
    const routeMeta = FIELD_METADATA.useSharedPhoneDriver;
    if (routeSupersedable && routeTouchedRef.current && routeMeta && routeMeta.number != null
        && !(String(routeMeta.number) in changed) && 'useSharedPhoneDriver' in baseline) {
      changed[routeMeta.number] = toWire(settings.useSharedPhoneDriver);
      logTransition('route-resave', viewFields(phoneTransition, { detail: `#128=${changed[routeMeta.number]}` }));
    }
    if (Object.keys(changed).length === 0) {
      setHasChanges(false);
      toast.info('No settings were changed');
      return;
    }
    const opts = { reason: 'settings page' };
    const rid = repostCaptionId();
    if (rid && changed[rid] !== undefined) {
      console.info(`${REPOST_CAPTION_TAG} V3 save ${slotName}: #${rid} sending ${describeTemplate(changed[rid])}`);
    }
    if (changedIdentity.length > 0) {
      // Identity fields need an explicit, deliberate confirmation - the server refuses them
      // otherwise. This is what stops a template/copy mishap from renaming an IG account.
      if (!window.confirm(`You are changing identity field(s): ${changedIdentity.join(', ')}. Apply deliberately?`)) {
        return;
      }
      opts.allowIdentity = true;
    }
    // R16H U1 (2026-10-12): 601 on with an empty 613 and no other repost source - WARN, never block: the save goes on.
    // r16sd (design SD28): with the Settings Doctor's save check on, its SD28 note replaces this toast, so a save never shows the
    // same warning twice; the card above the settings stays. Review SF5: decided AFTER the answer - the toast is held back here and
    // shown by the save check unless the check actually showed SD28 (a check switched off on the server, a save that did not touch
    // 601 / 613, or a check that could not be read all still say it once).
    let u1 = null;
    if (blankSourceWarn) {
      if (DOC_SAVE) {
        u1 = blankSourceWarn;
      } else {
        (toast.warning || toast.info)(blankSourceWarn.title, { description: blankSourceWarn.text, duration: 15000 });
        console.info(`${blankSourceWarn.logLine} (save of ${slotName})`);
      }
    }
    updateMutation.mutate({ values: changed, opts, u1 });
  };

  // Revert one recorded change (from the History dialog) through the same engine.
  const revertMutation = useMutation({
    mutationFn: (changeId) => settingsWriteApi.revert(slotName, changeId),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['slotSettings', slotName]);
      refetchHistory();
      toast.success(`Reverted ${res.data?.changed ?? 0} setting(s)`);
    },
    onError: (error) => toast.error(error.response?.data?.error || 'Revert failed'),
  });

  // Save the current form as a named TEMPLATE (server-side; identity fields are stripped there).
  const handleSaveAsPreset = async () => {
    if (!presetName.trim()) {
      toast.error('Please enter a template name');
      return;
    }
    try {
      const processedSettings = applyAutoSettings(settings);
      const values = {};
      Object.entries(FIELD_METADATA).forEach(([field, meta]) => {
        if (meta.number === undefined || meta.number === null) return;
        if (!baseline || !(field in baseline)) return;
        values[meta.number] = toWire(processedSettings[field]);
      });
      const res = await templatesApi.create({
        name: presetName.trim(),
        description: presetDescription.trim() || null,
        values,
      });
      toast.success(`Template "${presetName}" saved (${res.data.valueCount} values; identity fields excluded)`);
      setSavePresetDialogOpen(false);
      setPresetName('');
      setPresetDescription('');
      refetchPresets();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save template');
    }
  };

  // Apply the selected template TO THIS SLOT - preview first, then the server applies it through
  // the write engine (recorded in History, undoable). Nothing touches the form's unsaved edits.
  const handleLoadPreset = async () => {
    if (!selectedPresetId) {
      toast.error('Please select a template');
      return;
    }
    const preset = presets?.find(p => p.id === selectedPresetId);
    if (!preset) return;
    try {
      const dry = await templatesApi.apply(preset.id, [slotName], true);
      const target = dry.data.targets?.[0];
      if (!target?.ok) {
        toast.error(`Cannot apply: ${target?.error || 'unknown error'}`);
        return;
      }
      if (target.changed === 0) {
        toast.info('This slot already matches the template');
        setLoadPresetDialogOpen(false);
        return;
      }
      if (!confirmDiscardingEdits(`Apply template "${preset.name}" to ${slotName}? ${target.changed} setting(s) will change (undo available in History).`)) {
        return;
      }
      await templatesApi.apply(preset.id, [slotName], false);
      toast.success(`Template "${preset.name}" applied: ${target.changed} setting(s) changed`);
      setLoadPresetDialogOpen(false);
      setSelectedPresetId('');
      queryClient.invalidateQueries(['slotSettings', slotName]);
      queryClient.invalidateQueries(['settingsHistory', slotName]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to apply template');
    }
  };

  const handleDeletePreset = async (presetId, name) => {
    if (!window.confirm(`Are you sure you want to delete template "${name}"?`)) {
      return;
    }
    try {
      await templatesApi.remove(presetId);
      toast.success('Template deleted');
      refetchPresets();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete template');
    }
  };

  const handleInputChange = (field, value) => {
    if (field === 'useSharedPhoneDriver' && routeSupersedable && !routeTouchedRef.current) {
      routeTouchedRef.current = true;
      logTransition('route-edit', viewFields(phoneTransition, { detail: `#128=${value}` }));
    }
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Get organized sections data
  /*
   * V1's OWN metadata, generated from slot-settings2.html. Two things come from it and both are
   * things this page previously guessed at or ignored entirely:
   *
   *   control  - which widget to draw. The heuristics below guess from the FIELD NAME
   *              (startsWith('enable'), includes('check'), startsWith('mute') && !'minute' ...),
   *              a V2 habit that renders 215 of V1's checkboxes as 200 switches and turns 48
   *              textareas into 64. V1's page is the authority on what a setting looks like.
   *   gatedBy  - the th:hidden conditions V1 wraps ~420 of the 637 controls in, so a customer
   *              only sees what their plan and their slot's features entitle them to.
   */
  // (the ['settingsMetadata'] query itself sits above the load effect - see "Round 14" there)
  const metaById = useMemo(() => {
    const list = serverMeta && (serverMeta.settings || serverMeta);
    const m = new Map();
    if (Array.isArray(list)) list.forEach((x) => m.set(x.id, x));
    return m;
  }, [serverMeta]);

  /*
   * Evaluate V1's gates. `th:hidden="${!flag}"` hides when the flag is falsy, and a flag the
   * legacy controller never puts in the model is null - which is also falsy, so it hides. That
   * asymmetry is deliberate here too: an unknown flag HIDES, matching V1 exactly rather than
   * exposing a setting the customer's plan does not include.
   */
  const hiddenByGate = useMemo(() => {
    const out = new Set();
    if (!metaById.size) return out;                    // metadata not in yet - hide nothing
    const userFlags = (me && me.flags) || {};
    const slotGates = (slotData && slotData.gates) || {};
    const values = (slotData && slotData.values) || {};
    const truthy = (v) => v === true || v === 'true' || v === '1' || v === 1;
    const flagValue = (name) => {
      const bare = name.indexOf('.') >= 0 ? name.split('.').pop() : name;
      if (Object.prototype.hasOwnProperty.call(slotGates, bare)) return !!slotGates[bare];
      if (Object.prototype.hasOwnProperty.call(userFlags, bare)) return !!userFlags[bare];
      const byField = FIELD_METADATA[bare];             // a gate that is itself a setting (#802)
      if (byField && byField.number != null) return truthy(values[String(byField.number)]);
      return false;                                     // never in the model -> null -> hidden
    };
    metaById.forEach((sm, id) => {
      const gates = sm.gatedBy || [];
      for (let i = 0; i < gates.length; i++) {
        const m = /^\$\{!\s*([A-Za-z0-9_.]+)\s*\}$/.exec(gates[i]);
        if (m && !flagValue(m[1])) { out.add(id); return; }
      }
    });
    return out;
  }, [metaById, me, slotData]);

  /*
   * R16H U1 (2026-10-12), reports/varun-2026-10-12 section 4E (lib/blankSourceWarning.js): 601 Enable Post Scraping on,
   * 613 Source List empty, no other repost source (664 with 679, 677, 697) - the slot scrapes nothing. A warning card
   * under the header while it holds, and a toast on save; never a block. Only values the server sent count (the baseline), and a
   * setting hidden by V1's gates counts as off, as on V1.
   */
  // R16H review (2026-10-13): list 2 counts as V1 and the server count it - it has no setting id, so the server's settings answer
  // says whether it holds a source (sourceList2HasSources; a server without the key: list 2 adds nothing, as in r16h).
  const list2HasSources = !!(slotData && slotData.sourceList2HasSources === true);
  const blankSourceWarn = useMemo(
    () => (baseline ? blankSourceWarning(valuesById(settings, baseline, FIELD_METADATA, hiddenByGate), { list2HasSources }) : null),
    [settings, baseline, hiddenByGate, list2HasSources],
  );
  const blankSourceWarnShown = !!blankSourceWarn;
  useEffect(() => {
    if (blankSourceWarnShown) console.info(`[R16H-BLANK-SOURCE] ${slotName}: 601 on, 613 empty, no other repost source - warning shown`);
  }, [blankSourceWarnShown, slotName]);

  /*
   * r16m (2026-10-20), design 2.9 (lib/batch1217Warning.js; switch r16mUiEnabled): while #1216 is on, a live hint under #1217 - empty,
   * new-line / ';' separators, a phone that is not one of the account's, this slot's own #7 phone missing, still the default, over 255
   * characters. Never a block: the server's save check is the real one (it refuses an empty #1217 with #1216 on, and its notes reach
   * the toast below). Only values the server sent count, and a setting hidden by V1's gates counts as off.
   */
  const batchHintOn = r16mUiEnabled();
  const batch1217Field = useMemo(() => fieldForId(BATCH_IDS.devices, FIELD_METADATA), []);
  const deviceIds = useMemo(() => (Array.isArray(devicesData) ? devicesData.map((d) => d && (d.id || d.device_id)).filter(Boolean) : null), [devicesData]);
  const batchById = useMemo(() => (batchHintOn && baseline ? batchValuesById(settings, baseline, FIELD_METADATA, hiddenByGate) : null),
    [batchHintOn, settings, baseline, hiddenByGate]);
  // the batch records (review 2026-10-20, design 2.9: "not a member of the batch its phones belong to", "differs from the batch's
  // phones"): read only while #1216 is on; an older server (no route) or an error just leaves those two notes out
  const batch1216On = !!(batchById && [true, 'true', 1, '1'].includes(typeof batchById[BATCH_IDS.combined] === 'string'
    ? batchById[BATCH_IDS.combined].trim().toLowerCase() : batchById[BATCH_IDS.combined]));
  const { data: fbBatchesRaw } = useQuery({
    queryKey: ['followBackBatches'],
    queryFn: () => followBackApi.batches().then((r) => r.data),
    enabled: batch1216On,
    retry: false,
    staleTime: 60000,
  });
  const fbBatches = useMemo(() => (fbBatchesRaw ? normaliseBatches(fbBatchesRaw) : null), [fbBatchesRaw]);
  const isAdminViewer = !!(me && (me.isAdmin === true || me.role === 'owner'));
  const batch1217Warn = useMemo(
    () => (batchById ? batch1217Warning(batchById, deviceIds, { batches: fbBatches, slot: slotName, admin: isAdminViewer }) : null),
    [batchById, deviceIds, fbBatches, slotName, isAdminViewer],
  );
  const batch1217Line = batch1217Warn ? batch1217Warn.logLine : null;
  useEffect(() => {
    if (batch1217Line) console.info(`${batch1217Line} (${slotName})`);
  }, [batch1217Line, slotName]);

  /*
   * r16n (2026-10-21), design D:/NecroWork/r15k10-cf-evidence/DESIGN.md 2.2 (lib/storyCloseFriends.js; switch r16nUiEnabled): a live
   * hint under the #1109 / #1110 "Probability Of Sharing To Close Friends" row while the pair is outside 0-100, Max is below Min, or a
   * value is not a whole number - what the server's clamp will store. Never a block: the server's clamp is the validation, and the
   * save's own auto-adjustment note follows it. Only a pair the server sent counts, and a pair hidden by V1's gates gets no hint.
   */
  const storyCfOn = r16nUiEnabled();
  const storyCfMinField = useMemo(() => fieldForId(STORY_CF_IDS.min, FIELD_METADATA), []);
  const storyCfMaxField = useMemo(() => fieldForId(STORY_CF_IDS.max, FIELD_METADATA), []);
  const storyCfWarn = useMemo(() => {
    if (!storyCfOn || !baseline || !storyCfMinField || !storyCfMaxField) return null;
    const sent = (f) => Object.prototype.hasOwnProperty.call(baseline, f);
    if (!sent(storyCfMinField) || !sent(storyCfMaxField)) return null;
    if (hiddenByGate.has(STORY_CF_IDS.min) || hiddenByGate.has(STORY_CF_IDS.max)) return null;
    return storyCfCheck(settings[storyCfMinField], settings[storyCfMaxField]);
  }, [storyCfOn, baseline, storyCfMinField, storyCfMaxField, hiddenByGate, settings]);
  const storyCfLine = storyCfWarn ? storyCfWarn.logLine : null;
  useEffect(() => {
    if (storyCfLine) console.info(`${storyCfLine} (${slotName})`);
  }, [storyCfLine, slotName]);

  /*
   * r16p (2026-10-24), design 5.6 / 7.3 (lib/dmInboxHint.js; switch r16pUiEnabled): while #776 "Open unread threads" is ticked, a hint
   * under it - opening a conversation shows its sender "Seen" on Instagram. #771-787 are server-defined (GET /uiapi/settings/metadata,
   * section "DM Inbox (Chats)"), so their field names are looked up by id once the metadata registered them. Never a block.
   */
  const dmHintOn = r16pUiEnabled();
  // the server-only field name: FIELD_METADATA once registered (the load effect does that before it sets the baseline), else the
  // metadata entry's own javaField (the name serverOnlyFields registers it under)
  const dmFieldFor = (id) => fieldForId(id, FIELD_METADATA) || (metaById.get(id) && metaById.get(id).javaField) || null;
  const dmOpenField = useMemo(() => (metaById && metaById.size ? dmFieldFor(DM_INBOX_IDS.openUnread) : null), [metaById, baseline]); // eslint-disable-line react-hooks/exhaustive-deps
  const dmThreadsField = useMemo(() => (metaById && metaById.size ? dmFieldFor(DM_INBOX_IDS.threadsPerSession) : null), [metaById, baseline]); // eslint-disable-line react-hooks/exhaustive-deps
  const dmSeenHint = useMemo(() => {
    if (!dmHintOn || !dmOpenField || !settings || hiddenByGate.has(DM_INBOX_IDS.openUnread)) return null;
    return dmOpenUnreadHint(settings[dmOpenField], dmThreadsField ? settings[dmThreadsField] : null);
  }, [dmHintOn, dmOpenField, dmThreadsField, settings, hiddenByGate]);
  const dmSeenLine = dmSeenHint ? dmSeenHint.logLine : null;
  useEffect(() => {
    if (dmSeenLine) console.info(`${dmSeenLine} (${slotName})`);
  }, [dmSeenLine, slotName]);

  const fieldsBySection = useMemo(() => {
    if (!settings) return {};
    const all = getFieldsBySection(settings);
    // r16n: a bundle field marked `onlyWhenSent` (#1109 / #1110) is drawn only when the server sent its value - against a server
    // before r16n it would be a 0 / 0 row that saves nowhere (an older server does not know the ids)
    const unsent = (f) => !!(f.meta && f.meta.onlyWhenSent) && !(baseline && Object.prototype.hasOwnProperty.call(baseline, f.name));
    const anyUnsent = Object.values(all).some((fields) => fields.some(unsent));
    if (!hiddenByGate.size && !anyUnsent) return all;
    const kept = {};
    Object.entries(all).forEach(([section, fields]) => {
      const keep = fields.filter((f) => !hiddenByGate.has(f.meta && f.meta.number) && !unsent(f));
      if (keep.length) kept[section] = keep;
    });
    return kept;
  }, [settings, hiddenByGate, baseline]);

  // Filter fields based on search
  const filteredFieldsBySection = useMemo(() => {
    if (!searchQuery) return fieldsBySection;
    
    const filtered = {};
    const searchLower = searchQuery.toLowerCase();
    const searchClean = searchQuery.replace(/^#/, '');
    
    Object.entries(fieldsBySection).forEach(([section, fields]) => {
      const matchingFields = fields.filter(field => {
        const meta = metaFor(field.name);
        const matchesField = field.name.toLowerCase().includes(searchLower);
        const matchesLabel = meta.label.toLowerCase().includes(searchLower);
        const matchesNumber = meta.number?.toString() === searchClean || meta.number?.toString().includes(searchClean);
        return matchesField || matchesLabel || matchesNumber;
      });
      if (matchingFields.length > 0) {
        filtered[section] = matchingFields;
      }
    });
    
    return filtered;
  }, [fieldsBySection, searchQuery, metaFor]);	// r16 combined: metaFor (the per-platform labels) is read above

  // Get ordered sections that have visible fields
  const orderedSections = useMemo(() => {
    const sections = Object.keys(filteredFieldsBySection);
    return SECTION_ORDER.filter(s => sections.includes(s))
      .concat(sections.filter(s => !SECTION_ORDER.includes(s)));
  }, [filteredFieldsBySection]);

  // The sections that actually have something to draw - what the accordion lists.
  const visibleSections = useMemo(
    () => orderedSections.filter((s) => filteredFieldsBySection[s]?.length > 0),
    [orderedSections, filteredFieldsBySection],
  );
  visibleSectionsRef.current = visibleSections;
  // Changes only when the LIST changes - not on every keystroke, which rebuilds the arrays above.
  const visibleSectionsKey = visibleSections.join('\n');

  // All-open accordion: the open list handed to Radix. Built from the key, so a keystroke gives
  // Radix the SAME array and its sections are not all redrawn (see AccordionSection).
  const openSections = useMemo(
    () => (visibleSectionsKey ? visibleSectionsKey.split('\n') : []).filter((s) => !closedSections.has(s)),
    [visibleSectionsKey, closedSections],
  );
  const registerSectionRef = useCallback((sectionName, el) => { sectionRefs.current[sectionName] = el; }, []);

  const accordionAllOpen = viewMode === VIEW_MODES.ACCORDION && !accordionSingle;

  /*
   * All-open accordion: the sidebar (and the phone chip strip) follows the scroll, highlighting the
   * section you are reading. A jump from the sidebar pauses it (spyLockRef) while the smooth scroll
   * passes the sections in between, so the clicked one stays highlighted - also when it is near the
   * bottom and cannot scroll all the way up. One-at-a-time mode keeps its old highlight rules.
   */
  useEffect(() => {
    if (!accordionAllOpen || !visibleSectionsKey) return undefined;
    const raf = window.requestAnimationFrame
      ? (cb) => window.requestAnimationFrame(cb)
      : (cb) => window.setTimeout(cb, 16);
    const cancel = window.cancelAnimationFrame
      ? (id) => window.cancelAnimationFrame(id)
      : (id) => window.clearTimeout(id);
    let frame = 0;
    /*
     * The line itself: SPY_LINE_PX, or - while the phone chip strip is showing - just below where
     * it pins. The strip pins under the top bar, and an iPhone home-screen app (viewport-fit=cover,
     * black-translucent) makes that bar taller by the notch inset, so a fixed line ends up UNDER the
     * strip. Measured 2026-09-29 at 390 px with --safe-top 47px: a chip jump landed its section at
     * 171 px, and the first touch after it moved the highlight back to the section above.
     */
    const spyLine = () => {
      const strip = chipStripRef.current;
      if (!strip || !strip.offsetHeight) return SPY_LINE_PX;   // no strip (lg and up: the sidebar)
      const pinnedTop = parseFloat(window.getComputedStyle(strip).top);
      if (!Number.isFinite(pinnedTop)) return SPY_LINE_PX;
      return Math.max(SPY_LINE_PX, pinnedTop + strip.offsetHeight + 20);
    };
    const update = () => {
      frame = 0;
      const sections = visibleSectionsRef.current;
      const line = spyLine();
      let current = null;
      for (let i = 0; i < sections.length; i++) {
        const el = sectionRefs.current[sections[i]];
        if (!el) continue;
        if (el.getBoundingClientRect().top <= line) current = sections[i];
        else break;
      }
      if (!current) current = sections[0] || null;
      setActiveSidebarSection((prev) => (prev === current ? prev : current));
    };
    const onScroll = () => {
      const now = Date.now();
      const lock = spyLockRef.current;
      if (now < lock.until) {
        // still the jump's own smooth scroll: keep the pause going until it settles (capped)
        lock.until = Math.min(lock.cap, Math.max(lock.until, now + 200));
        return;
      }
      if (!frame) frame = raf(update);
    };
    // The user taking over (wheel, touch, keys) ends a jump's pause at once. Measured 2026-09-29 at
    // 390 px: without this, a wheel scroll that began before the jump's long smooth scroll had
    // settled kept extending the pause, and the highlight stayed on the jumped-to section.
    const onUserScroll = () => {
      if (spyLockRef.current.until) spyLockRef.current = { until: 0, cap: 0 };
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('wheel', onUserScroll, { passive: true });
    window.addEventListener('touchstart', onUserScroll, { passive: true });
    window.addEventListener('keydown', onUserScroll);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('wheel', onUserScroll);
      window.removeEventListener('touchstart', onUserScroll);
      window.removeEventListener('keydown', onUserScroll);
      if (frame) cancel(frame);
    };
  }, [accordionAllOpen, visibleSectionsKey]);

  // ...and the highlighted entry is scrolled into view inside its own list (never the window).
  useEffect(() => {
    if (!accordionAllOpen || !activeSidebarSection) return;
    keepItemInView(sidebarListRef.current, sidebarItemRefs.current[activeSidebarSection], false);
    keepItemInView(chipStripRef.current, chipRefs.current[activeSidebarSection], true);
  }, [accordionAllOpen, activeSidebarSection]);

  // Initialize active tab when sections load
  React.useEffect(() => {
    if (orderedSections.length > 0 && !activeTab) {
      setActiveTab(orderedSections[0]);
    }
  }, [orderedSections, activeTab]);

  // Initialize active sub-tab for nested view
  React.useEffect(() => {
    if (activeCategory && MAIN_CATEGORIES[activeCategory]) {
      const availableSubs = MAIN_CATEGORIES[activeCategory].filter(s => filteredFieldsBySection[s]);
      if (availableSubs.length > 0 && (!activeSubTab || !availableSubs.includes(activeSubTab))) {
        setActiveSubTab(availableSubs[0]);
      }
    }
  }, [activeCategory, filteredFieldsBySection, activeSubTab]);

  // Handle sidebar section click in accordion view
  const handleSidebarSectionClick = useCallback((sectionName) => {
    setActiveSidebarSection(sectionName);
    if (accordionSingle) {
      // one at a time: opening this one closes the one before (the behaviour before 2026-09-29)
      setOpenAccordionSection(sectionName);
    } else {
      // all open: make sure this one is open (it may have been closed) and leave the rest alone
      const now = Date.now();
      spyLockRef.current = { until: now + 1000, cap: now + 3000 };
      setClosedSections((prev) => {
        if (!prev.has(sectionName)) return prev;
        const next = new Set(prev);
        next.delete(sectionName);
        return next;
      });
    }

    // Scroll to section with a small delay to allow accordion to open
    setTimeout(() => {
      const element = sectionRefs.current[sectionName];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, [accordionSingle]);

  // The "One section at a time" checkbox.
  const handleAccordionSingleChange = useCallback((checked) => {
    const single = checked === true;
    console.info('[slot-settings] accordion:', single ? 'one section at a time' : 'every section open');
    setAccordionSingle(single);
    saveAccordionSingle(single);
    if (single) {
      // keep open the section you were in (the highlighted one), and only that
      setOpenAccordionSection((prev) => {
        const keep = activeSidebarSection || prev;
        return keep && visibleSectionsRef.current.includes(keep) ? keep : null;
      });
    } else {
      setClosedSections(new Set());
    }
  }, [activeSidebarSection]);

  // All-open accordion: a header was clicked. Radix reports the open list; keep the closed one.
  const handleAccordionMultiChange = useCallback((openNow) => {
    const open = new Set(openNow);
    setClosedSections((prev) => {
      const next = new Set(prev);
      visibleSectionsRef.current.forEach((s) => { if (open.has(s)) next.delete(s); else next.add(s); });
      return next;
    });
  }, []);

  const handleExpandAll = useCallback(() => setClosedSections(new Set()), []);
  const handleCollapseAll = useCallback(() => setClosedSections(new Set(visibleSectionsRef.current)), []);

  // All view: every section is already open, so a jump is only a scroll - no state changes.
  const handleJumpToSection = useCallback((sectionName) => {
    const element = allSectionRefs.current[sectionName];
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleBackToSectionList = useCallback(() => {
    if (allJumpStripRef.current) allJumpStripRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const renderField = (fieldName, value) => {
    const meta = metaFor(fieldName);	// r16: the per-platform label (userName on a TikTok slot)
    /*
     * V1's control wins whenever V1 has one. All the name-guessing further down is kept only for
     * the settings that are NOT on the legacy page at all (`inProductionUi: false`), where there
     * is nothing to defer to.
     */
    const v1 = metaById.get(meta?.number);
    const v1Control = v1 && v1.inProductionUi ? v1.control : null;
    const v1Options = (v1 && v1.options) || null;
    const nameLower = fieldName.toLowerCase();
    
    // Skip hidden/internal settings
    if (meta.hidden) {
      return null;
    }

    // Skip if this is a Max field (we'll render it with Min)
    if (fieldName.endsWith('Max') || fieldName.endsWith('max')) {
      return null;
    }

    // Special case: deviceID should be a dropdown selector of active devices
    if (fieldName === 'deviceID') {
      const devices = devicesData || [];
      return (
        <div key={fieldName} className="space-y-2">
          <Label className="flex items-center gap-2 flex-wrap">
            {meta.number && (
              <span className="text-[11px] font-mono tnum text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                #{meta.number}
              </span>
            )}
            <span className="text-sm text-muted-foreground">{meta.label}</span>
            {meta.tooltip && (
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-accent cursor-help shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[calc(100vw-2rem)] sm:max-w-md bg-popover/95 backdrop-blur-sm border border-border text-foreground p-3 shadow-1">
                    <p className="text-sm text-foreground" dangerouslySetInnerHTML={{ __html: meta.tooltip }} />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </Label>
          <Select
            value={value || '__none__'}
            onValueChange={(newValue) => handleInputChange(fieldName, newValue === '__none__' ? '' : newValue)}
          >
            <SelectTrigger className="bg-secondary/60 border-border text-foreground" data-testid={`setting-${fieldName}`}>
              <SelectValue placeholder="Select a device..." />
            </SelectTrigger>
            <SelectContent className="border-border">
              <SelectItem value="__none__" className="text-foreground">None (No shared device)</SelectItem>
              {devices.map((device) => (
                <SelectItem key={device.id} value={device.id} className="text-foreground">
                  {device.label || device.device_name} ({device.device_id || `device${device.device_number}`})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Special case: timezome should be a dropdown selector of timezones
    if (fieldName === 'timezome') {
      // Sorted by GMT offset from lowest to highest
      const timezones = [
        { value: 'Pacific/Honolulu', label: '(GMT -10:00) Pacific/Honolulu - Hawaii Time' },
        { value: 'America/Anchorage', label: '(GMT -9:00) America/Anchorage - Alaska Time' },
        { value: 'America/Los_Angeles', label: '(GMT -8:00) America/Los_Angeles - Pacific Time' },
        { value: 'America/Vancouver', label: '(GMT -8:00) America/Vancouver - Pacific Time' },
        { value: 'America/Denver', label: '(GMT -7:00) America/Denver - Mountain Time' },
        { value: 'America/Chicago', label: '(GMT -6:00) America/Chicago - Central Time' },
        { value: 'America/Mexico_City', label: '(GMT -6:00) America/Mexico_City - Central Time' },
        { value: 'America/New_York', label: '(GMT -5:00) America/New_York - Eastern Time' },
        { value: 'America/Toronto', label: '(GMT -5:00) America/Toronto - Eastern Time' },
        { value: 'America/Sao_Paulo', label: '(GMT -3:00) America/Sao_Paulo - Brasilia Time' },
        { value: 'America/Buenos_Aires', label: '(GMT -3:00) America/Buenos_Aires - Argentina Time' },
        { value: 'UTC', label: '(GMT +0:00) UTC - Coordinated Universal Time' },
        { value: 'Europe/London', label: '(GMT +0:00) Europe/London - GMT/BST' },
        { value: 'Africa/Lagos', label: '(GMT +1:00) Africa/Lagos - West Africa Time' },
        { value: 'Europe/Paris', label: '(GMT +1:00) Europe/Paris - Central European Time' },
        { value: 'Europe/Berlin', label: '(GMT +1:00) Europe/Berlin - Central European Time' },
        { value: 'Europe/Rome', label: '(GMT +1:00) Europe/Rome - Central European Time' },
        { value: 'Europe/Madrid', label: '(GMT +1:00) Europe/Madrid - Central European Time' },
        { value: 'Europe/Amsterdam', label: '(GMT +1:00) Europe/Amsterdam - Central European Time' },
        { value: 'Europe/Brussels', label: '(GMT +1:00) Europe/Brussels - Central European Time' },
        { value: 'Europe/Vienna', label: '(GMT +1:00) Europe/Vienna - Central European Time' },
        { value: 'Europe/Warsaw', label: '(GMT +1:00) Europe/Warsaw - Central European Time' },
        { value: 'Europe/Prague', label: '(GMT +1:00) Europe/Prague - Central European Time' },
        { value: 'Europe/Stockholm', label: '(GMT +1:00) Europe/Stockholm - Central European Time' },
        { value: 'Europe/Oslo', label: '(GMT +1:00) Europe/Oslo - Central European Time' },
        { value: 'Africa/Cairo', label: '(GMT +2:00) Africa/Cairo - Eastern European Time' },
        { value: 'Africa/Johannesburg', label: '(GMT +2:00) Africa/Johannesburg - South Africa Time' },
        { value: 'Europe/Helsinki', label: '(GMT +2:00) Europe/Helsinki - Eastern European Time' },
        { value: 'Europe/Athens', label: '(GMT +2:00) Europe/Athens - Eastern European Time' },
        { value: 'Europe/Istanbul', label: '(GMT +3:00) Europe/Istanbul - Turkey Time' },
        { value: 'Europe/Moscow', label: '(GMT +3:00) Europe/Moscow - Moscow Time' },
        { value: 'Asia/Dubai', label: '(GMT +4:00) Asia/Dubai - Gulf Standard Time' },
        { value: 'Asia/Kolkata', label: '(GMT +5:30) Asia/Kolkata - India Standard Time' },
        { value: 'Asia/Dhaka', label: '(GMT +6:00) Asia/Dhaka - Bangladesh Time' },
        { value: 'Asia/Bangkok', label: '(GMT +7:00) Asia/Bangkok - Indochina Time' },
        { value: 'Asia/Jakarta', label: '(GMT +7:00) Asia/Jakarta - Western Indonesia Time' },
        { value: 'Asia/Singapore', label: '(GMT +8:00) Asia/Singapore - Singapore Time' },
        { value: 'Asia/Hong_Kong', label: '(GMT +8:00) Asia/Hong_Kong - Hong Kong Time' },
        { value: 'Asia/Shanghai', label: '(GMT +8:00) Asia/Shanghai - China Standard Time' },
        { value: 'Asia/Manila', label: '(GMT +8:00) Asia/Manila - Philippine Time' },
        { value: 'Australia/Perth', label: '(GMT +8:00) Australia/Perth - Australian Western Time' },
        { value: 'Asia/Tokyo', label: '(GMT +9:00) Asia/Tokyo - Japan Standard Time' },
        { value: 'Asia/Seoul', label: '(GMT +9:00) Asia/Seoul - Korea Standard Time' },
        { value: 'Australia/Sydney', label: '(GMT +10:00) Australia/Sydney - Australian Eastern Time' },
        { value: 'Australia/Melbourne', label: '(GMT +10:00) Australia/Melbourne - Australian Eastern Time' },
        { value: 'Australia/Brisbane', label: '(GMT +10:00) Australia/Brisbane - Australian Eastern Time' },
        { value: 'Pacific/Auckland', label: '(GMT +12:00) Pacific/Auckland - New Zealand Time' },
      ];
      
      return (
        <div key={fieldName} className="space-y-2">
          <Label className="flex items-center gap-2 flex-wrap">
            {meta.number && (
              <span className="text-[11px] font-mono tnum text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                #{meta.number}
              </span>
            )}
            <span className="text-sm text-muted-foreground">{meta.label}</span>
            {meta.tooltip && (
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-accent cursor-help shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[calc(100vw-2rem)] sm:max-w-md bg-popover/95 backdrop-blur-sm border border-border text-foreground p-3 shadow-1">
                    <p className="text-sm text-foreground" dangerouslySetInnerHTML={{ __html: meta.tooltip }} />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </Label>
          <Select
            value={value || 'UTC'}
            onValueChange={(newValue) => handleInputChange(fieldName, newValue)}
          >
            <SelectTrigger className="bg-secondary/60 border-border text-foreground" data-testid={`setting-${fieldName}`}>
              <SelectValue placeholder="Select timezone..." />
            </SelectTrigger>
            <SelectContent className="border-border max-h-80">
              {timezones.map((tz) => (
                <SelectItem key={tz.value} value={tz.value} className="text-foreground">
                  {tz.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    /*
     * aeroplaneModeToggleType (#100) and automationVariantType (#821) were hardcoded dropdowns
     * here. Both invented their own option set, and neither matched V1.
     *
     *   #821  V3 offered 0..3 as "Standard Automation (Default)" / "Conservative Mode" /
     *         "Aggressive Mode" / "Custom Profile". V1 offers 1..7, labelled plainly
     *         "1 (default)".."7". The product has never had a "Conservative Mode" - those names
     *         were invented. So a slot storing 1, which is 11,043 of the 11,044 that exist,
     *         displayed as "Conservative Mode", and the single slot storing 5 matched no option
     *         at all and left the control sitting on its placeholder.
     *
     *   #100  V3 offered "0","2","3","4". V1 stores "default", "2_toggleJustMobileData",
     *         "3_viaUI", "4_directSwipeDowns" - a different VALUE SPACE, not just different
     *         labels. Choosing one here wrote a value V1's own select cannot display.
     *
     * The generic branch below already asks the server for V1's control and options
     * (GET /uiapi/settings/metadata, generated from slot-settings2.html), so neither field ever
     * needed a special case - the hardcoding only shadowed the correct data. Deleting them makes
     * both track V1 on their own, including the options V1 has commented out.
     *
     * ROLLBACK: git revert. Frontend only - no data migration and no server change.
     */

    // Special case: autoLoginPlusProfileEditingConfig should be a dropdown
    if (fieldName === 'autoLoginPlusProfileEditingConfig') {
      const configOptions = [
        { value: '0', label: '0: Do nothing (no auto-login or profile editing)' },
        { value: '1', label: '1: Only login new account from sheet' },
        { value: '2', label: '2: Only update profile editing settings' },
        { value: '3', label: '3: Auto edit profile after logging in new account' },
        { value: '4', label: '4: Auto edit profile after 12-16 days' },
      ];
      
      return (
        <div key={fieldName} className="space-y-2">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <span className="text-xs text-muted-foreground">#{meta.number}</span>
            {meta.label}
            {meta.tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p className="text-xs" dangerouslySetInnerHTML={{ __html: meta.tooltip }} />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </label>
          <Select
            value={String(value || '0')}
            onValueChange={(newValue) => handleInputChange(fieldName, newValue)}
          >
            <SelectTrigger className="bg-secondary/60 border-border text-foreground focus:ring-ring">
              <SelectValue placeholder="Select config" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border text-popover-foreground z-50 max-h-60 overflow-y-auto">
              {configOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-foreground hover:bg-accent focus:bg-accent">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Generic select dropdown for fields with type: "select" and options in metadata
    if ((meta?.type === 'select' && meta?.options) || (v1Control === 'select' && v1Options && v1Options.length)) {
      // Options and selection both live in ../lib/settingsOptions - see that file for why the
      // page must never define an option set of its own.
      const normalizedOptions = selectOptions(meta, v1Options);
      const currentValue = currentOptionValue(value, normalizedOptions);
      
      return (
        <div key={fieldName} className="space-y-2">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <span className="text-xs text-muted-foreground">#{meta.number}</span>
            {meta.label}
            {meta.tooltip && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-sm">
                    <p className="text-xs" dangerouslySetInnerHTML={{ __html: meta.tooltip }} />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </label>
          <Select
            value={currentValue}
            onValueChange={(newValue) => handleInputChange(fieldName, newValue)}
          >
            <SelectTrigger className="bg-secondary/60 border-border text-foreground focus:ring-ring" data-testid={`setting-${fieldName}`}>
              <SelectValue placeholder="Select option..." />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border text-popover-foreground z-50 max-h-60 overflow-y-auto">
              {normalizedOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-foreground hover:bg-accent focus:bg-accent">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // Textarea fields (multi-line text) - check first
    const isTextarea = nameLower.includes('spintax') || 
                       nameLower.includes('spintext') || 
                       nameLower.includes('grid') ||
                       nameLower.includes('list') && !nameLower.includes('directfollow') && !nameLower.includes('blacklist') && !nameLower.includes('whitelist') && !nameLower.includes('followedlist') ||
                       nameLower.includes('csv') ||
                       nameLower.includes('data') && !nameLower.includes('datasaver') ||
                       nameLower.includes('template') ||
                       nameLower.includes('xpath') ||
                       nameLower.includes('workflow') ||
                       nameLower.includes('words') ||
                       nameLower.includes('urls') && !nameLower.startsWith('start') && !nameLower.startsWith('dont') && !nameLower.startsWith('schedule');
    
    // Text fields - explicit patterns for string inputs
    const isTextField = nameLower.includes('username') && !nameLower.includes('usernames') ||
                        nameLower.includes('password') ||
                        nameLower.includes('name') && !nameLower.includes('filename') && !nameLower.includes('usernames') ||
                        nameLower.includes('url') && !nameLower.startsWith('enable') && !nameLower.startsWith('start') && !nameLower.startsWith('schedule') && !nameLower.startsWith('dont') ||
                        nameLower.includes('package') ||
                        nameLower.includes('activity') ||
                        nameLower.includes('path') ||
                        nameLower.includes('caption') && !nameLower.startsWith('enable') && !nameLower.startsWith('remove') && !nameLower.startsWith('use') && !nameLower.startsWith('prepend') ||
                        nameLower.includes('hashtag') && !nameLower.startsWith('remove') ||
                        nameLower.includes('message') ||
                        nameLower.includes('bio') ||
                        nameLower.includes('text') && !nameLower.includes('spintext') && !nameLower.startsWith('prepend') && !nameLower.startsWith('add') ||
                        nameLower.includes('title') ||
                        nameLower.includes('description') ||
                        nameLower.includes('email') ||
                        nameLower.includes('phone') && !nameLower.startsWith('dont') && !nameLower.startsWith('use') ||
                        nameLower.includes('address') ||
                        nameLower.includes('link') ||
                        nameLower.includes('key') && !nameLower.includes('keyboard') && !nameLower.includes('keyboad') && !nameLower.includes('keybar') ||
                        nameLower.includes('token') ||
                        nameLower.includes('id') && !nameLower.includes('valid') && !nameLower.includes('userid') && !nameLower.includes('friday') && !nameLower.includes('video') ||
                        nameLower.includes('code') ||
                        nameLower.includes('label') ||
                        nameLower.includes('format') ||
                        nameLower.includes('pattern') ||
                        nameLower.includes('prefix') ||
                        nameLower.includes('suffix') ||
                        nameLower.includes('separator') ||
                        nameLower.includes('delimiter') ||
                        nameLower.endsWith('time') && !nameLower.includes('timeout') && !nameLower.includes('sometime');
    
    // Number fields - be more specific
    const isNumber = !isTextarea && !isTextField && (
      nameLower.includes('min') && !nameLower.includes('minute') && !nameLower.includes('admin') && !nameLower.includes('terminal') ||
      nameLower.includes('max') && !nameLower.includes('maxim') ||
      nameLower.includes('limit') ||
      nameLower.includes('count') && !nameLower.includes('account') ||
      nameLower.includes('seconds') ||
      nameLower.includes('hours') && !nameLower.includes('afterhowmany') && !nameLower.includes('unfollowonly') ||
      nameLower.includes('days') && !nameLower.includes('monday') && !nameLower.includes('tuesday') && !nameLower.includes('wednesday') && !nameLower.includes('thursday') && !nameLower.includes('friday') && !nameLower.includes('saturday') && !nameLower.includes('sunday') ||
      nameLower.includes('probability') ||
      nameLower.includes('percentage') ||
      nameLower.includes('ratio') ||
      nameLower.includes('number') && !nameLower.includes('phonenumber') ||
      nameLower.includes('amount') ||
      nameLower.includes('delay') ||
      nameLower.includes('timeout') ||
      nameLower.includes('interval') ||
      nameLower.includes('duration') ||
      nameLower.includes('upto') ||
      nameLower.includes('buffer') ||
      nameLower.includes('multiplier') ||
      nameLower.includes('avgtype') ||
      nameLower.endsWith('perday') ||
      nameLower.endsWith('perhour') ||
      nameLower.endsWith('perweek') ||
      nameLower.endsWith('persession') ||
      meta?.type === 'number'
    );

    // Boolean fields - only if not already identified as text/textarea/number
    // FIRST: Check for explicit boolean fields that should ALWAYS be toggles
    const explicitBooleanFields = [
      'useAdbKeyboad',         // #801
      'screenRecordSessions',  // #808
      'deleteNonErroneousSessionRecordings', // #809
      'takeScreenshotWhenErrOccurs', // #810
      'enableUIDebugMode',     // #811
      'keyboardNeverComesUp',  // #812
      'onlySmallKeybarPopsUp', // #815
      'reinstallAutomationApkOnCrashes', // #827
      'switchUsersViaCmd',     // #833
    ];
    const isExplicitBoolean = explicitBooleanFields.includes(fieldName) || meta?.type === 'boolean';
    
    const isBoolean = isExplicitBoolean || (!isTextarea && !isTextField && !isNumber && (
                      nameLower.includes('enabled') || 
                      nameLower.startsWith('enable') || 
                      nameLower.startsWith('dowe') ||
                      nameLower.startsWith('do_we') ||
                      nameLower.includes('_dowe') ||
                      nameLower.includes('_stop') ||
                      nameLower.startsWith('do') && nameLower.length > 2 && /^do[A-Z0-9]/.test(fieldName) ||
                      nameLower.includes('_enabled') ||
                      nameLower.includes('ison') ||
                      nameLower.includes('is_on') ||
                      nameLower.includes('check') && !nameLower.includes('uncheck') && !nameLower.includes('checkupto') && !nameLower.includes('checkx') ||
                      nameLower.startsWith('is') && nameLower.length > 2 && /^is[A-Z]/.test(fieldName) ||
                      nameLower.startsWith('use') && nameLower.length > 3 && /^use[A-Z]/.test(fieldName) && !nameLower.includes('user') ||
                      nameLower.startsWith('allow') ||
                      nameLower.startsWith('skip') ||
                      nameLower.startsWith('mute') && !nameLower.includes('minute') ||
                      nameLower.includes('_mute') && !nameLower.includes('minute') ||
                      nameLower.includes('mutepostsafter') ||
                      nameLower.startsWith('auto') && !nameLower.includes('automation') ||
                      nameLower.startsWith('force') ||
                      nameLower.startsWith('attemp') ||
                      nameLower.startsWith('trigger') ||
                      nameLower.startsWith('send') && nameLower.includes('directly') ||
                      nameLower.startsWith('direct') && nameLower.includes('follow') ||
                      nameLower.startsWith('dont') ||
                      nameLower.startsWith('start') && nameLower.includes('aswell') ||
                      nameLower.startsWith('schedule') && nameLower.includes('order') ||
                      nameLower.startsWith('schdule') && nameLower.includes('order') ||
                      nameLower.startsWith('pick') ||
                      nameLower.startsWith('ignore') ||
                      nameLower.startsWith('engonlyif') ||
                      nameLower.startsWith('remove') && !nameLower.includes('removed') ||
                      nameLower.includes('_treat') ||
                      nameLower.includes('unfollowonly') ||
                      nameLower.includes('toggle') ||
                      nameLower.endsWith('only') ||
                      nameLower.startsWith('shift') ||
                      nameLower.startsWith('accept') ||
                      nameLower.startsWith('also') ||
                      nameLower.startsWith('hijack') ||
                      nameLower.startsWith('clear') ||
                      nameLower.startsWith('close') ||
                      nameLower.startsWith('operate') ||
                      nameLower.startsWith('run') && !nameLower.includes('running') ||
                      nameLower.startsWith('type') && nameLower.includes('text') ||
                      nameLower.startsWith('update') ||
                      nameLower.startsWith('reset') ||
                      nameLower.startsWith('highlight') && nameLower.includes('slotid') ||
                      nameLower.startsWith('substract') ||
                      nameLower.startsWith('subtract') ||
                      nameLower.startsWith('turn') ||
                      typeof value === 'boolean'));

    // Check if this is a Min field with corresponding Max
    const isMinField = fieldName.endsWith('Min') || fieldName.endsWith('min');
    
    // Check if this is a time field (executionAt fields are time-based)
    const isTimeField = nameLower.includes('executionat') || 
                        nameLower.includes('starthour') || 
                        nameLower.includes('endhour');
    
    let maxFieldName = null;
    let maxValue = null;
    let maxMeta = null;

    if (isMinField) {
      maxFieldName = maxPartnerOf(fieldName);

      if (maxFieldName && settings[maxFieldName] !== undefined) {
        maxValue = settings[maxFieldName];
        maxMeta = getFieldMeta(maxFieldName);
      }
    }

    // Render Min/Max pair together
    if (isMinField && maxFieldName && maxValue !== null) {
      const baseFieldName = fieldName.replace(/Min$|min$/, '');
      const baseMeta = getFieldMeta(baseFieldName);
      
      const displayLabel = (baseMeta.label && baseMeta.label !== baseFieldName) 
        ? baseMeta.label 
        : meta.label.replace(/ \(Min\)$| Min$|Min$/i, '').replace(/\(Min\)/i, '').trim();
      
      // Helper function to convert minutes to HH:MM format for time fields
      // #51-#54 are LocalTime on the server, NOT minutes since midnight. The V2 helpers that used
      // to live here assumed minutes, which rendered 23:55 as "00:23" and sent "1430" back - a
      // DateTimeParseException that failed the whole save. See lib/settingsTime.js.
      // A cleared input yields null and is IGNORED: '' is a 400 and the "null" sentinel is a 500
      // on these fields, so the only safe answer is to leave the stored time alone.
      const setTime = (name, text) => {
        const wire = fromTimeInput(text);
        if (wire !== null) handleInputChange(name, wire);
      };
      
      const pairRow = (
        <div key={fieldName} className="flex flex-col gap-3 p-3 rounded-xl bg-secondary/60 hover:bg-secondary/80 transition-colors sm:flex-row sm:items-center sm:justify-between sm:gap-4 col-span-full">
          <Label className="flex items-center gap-2 flex-wrap flex-1 min-w-0 cursor-pointer">
            {meta.number && (
              <span className="text-[11px] font-mono tnum text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
                #{meta.number}
              </span>
            )}
            <span className="text-sm text-muted-foreground">{displayLabel}</span>
            {meta.tooltip && (
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-accent cursor-help shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-[calc(100vw-2rem)] sm:max-w-md bg-popover/95 backdrop-blur-sm border border-border text-foreground p-3 shadow-1">
                    <p className="text-sm text-foreground" dangerouslySetInnerHTML={{ __html: meta.tooltip }} />
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </Label>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Min</span>
              {isTimeField ? (
                <Input
                  type="time"
                  value={toTimeInput(value)}
                  onChange={(e) => setTime(fieldName, e.target.value)}
                  className="bg-background border-border text-sm w-28"
                  data-testid={`setting-${fieldName}`}
                />
              ) : (
                <Input
                  type="number"
                  value={value ?? ''}
                  onChange={(e) => handleInputChange(fieldName, parseFloat(e.target.value) || 0)}
                  className="bg-background border-border text-sm w-20 text-right"
                  data-testid={`setting-${fieldName}`}
                />
              )}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-muted-foreground">Max</span>
              {maxMeta.number && maxMeta.number !== meta.number && (
                <span className="text-xs font-mono tnum text-primary">#{maxMeta.number}</span>
              )}
              {isTimeField ? (
                <Input
                  type="time"
                  value={toTimeInput(maxValue)}
                  onChange={(e) => setTime(maxFieldName, e.target.value)}
                  className="bg-background border-border text-sm w-28"
                  data-testid={`setting-${maxFieldName}`}
                />
              ) : (
                <Input
                  type="number"
                  value={maxValue ?? ''}
                  onChange={(e) => handleInputChange(maxFieldName, parseFloat(e.target.value) || 0)}
                  className="bg-background border-border text-sm w-20 text-right"
                  data-testid={`setting-${maxFieldName}`}
                />
              )}
            </div>
          </div>
        </div>
      );
      // r16n: the live #1109 / #1110 hint, under the pair row (the row itself is drawn exactly as every other pair)
      if (fieldName === storyCfMinField && storyCfWarn) {
        const err = storyCfWarn.severity === 'error';
        return (
          <div key={fieldName} className="col-span-full space-y-2">
            {pairRow}
            <div role="status" data-testid="story-cf-hint" data-severity={storyCfWarn.severity}
              className={`rounded-xl border p-3 text-sm ${err ? 'border-destructive/40 bg-destructive/10' : 'border-warning/40 bg-warning/10'}`}>
              <p className={`flex items-center gap-2 font-semibold ${err ? 'text-destructive' : 'text-warning'}`}>
                <Info className="h-4 w-4 shrink-0" aria-hidden />
                {storyCfWarn.title}
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-5 text-foreground">
                {storyCfWarn.items.map((it) => <li key={it.code} data-testid={`story-cf-${it.code}`}>{it.text}</li>)}
              </ul>
              <p className="mt-1.5 text-xs text-muted-foreground">
                The server saves each value as a whole number from 0 to 100, with Max at least Min, as V1 does: 50 / 50 (the default)
                sends about half the shares to Close Friends, 0 / 0 every share to Your story, 100 / 100 every share to Close Friends.
              </p>
            </div>
          </div>
        );
      }
      return pairRow;
    }

    const fieldLabel = (
      <div className="flex items-center gap-2 flex-wrap">
        {meta.number && (
          <span className="text-[11px] font-mono tnum text-primary bg-primary/10 px-1.5 py-0.5 rounded shrink-0">
            #{meta.number}
          </span>
        )}
        <span className="text-sm text-muted-foreground flex-1">{meta.label}</span>
        {meta.tooltip && (
          <TooltipProvider>
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-accent cursor-help shrink-0" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[calc(100vw-2rem)] sm:max-w-md bg-popover/95 backdrop-blur-sm border border-border text-foreground p-3 shadow-1">
                <p className="text-sm text-foreground" dangerouslySetInnerHTML={{ __html: meta.tooltip }} />
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );

    // V1's control decides; the name heuristics are the fallback for settings V1 never drew.
    const ctlBoolean  = v1Control ? v1Control === 'checkbox' : isBoolean;
    const ctlTextarea = v1Control ? v1Control === 'textarea' : isTextarea;
    const ctlNumber   = v1Control ? (v1Control === 'number' || v1Control === 'number:int') : isNumber;

    if (ctlBoolean) {
      // Convert value to boolean - handles "", "false", "true", etc.
      const boolValue = value === true || value === 'true' || value === '1';
      // r16p: #776 ticked -> the "Seen" hint under the row (lib/dmInboxHint.js), inside the same grid cell
      if (fieldName === dmOpenField && dmSeenHint) {
        // the whole row, so the hint never stretches the neighbouring cell of the grid
        return (
          <div key={fieldName} className="space-y-2 col-span-full">
            <div className="flex items-center justify-between p-3 rounded-xl bg-secondary/60 hover:bg-secondary/80 transition-colors">
              <Label htmlFor={fieldName} className="cursor-pointer flex-1 min-w-0">
                {fieldLabel}
              </Label>
              <Switch
                id={fieldName}
                checked={boolValue}
                onCheckedChange={(checked) => handleInputChange(fieldName, checked)}
                data-testid={`setting-${fieldName}`}
              />
            </div>
            <div role="status" data-testid="dm-seen-hint" className="rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm">
              <p className="flex items-center gap-2 font-semibold text-warning">
                <Info className="h-4 w-4 shrink-0" aria-hidden />
                {dmSeenHint.title}
              </p>
              <p className="mt-1 text-foreground/90 text-pretty">{dmSeenHint.text}</p>
            </div>
          </div>
        );
      }
      return (
        <div key={fieldName} className="flex items-center justify-between p-3 rounded-xl bg-secondary/60 hover:bg-secondary/80 transition-colors">
          <Label htmlFor={fieldName} className="cursor-pointer flex-1 min-w-0">
            {fieldLabel}
          </Label>
          <Switch
            id={fieldName}
            checked={fieldName === 'useSharedPhoneDriver' && routeLocked ? phoneTransition.requestedShared : boolValue}
            disabled={fieldName === 'useSharedPhoneDriver' && (routeLocked || routeActionPending)}
            onCheckedChange={(checked) => handleInputChange(fieldName, checked)}
            data-testid={`setting-${fieldName}`}
          />
        </div>
      );
    } else if (ctlTextarea) {
      /*
       * r16h (2026-10-12): a server setting may carry plain-text `help` and a `placeholder` (first: 723 "Repost Caption
       * Template"). Both come from the server's metadata; help is drawn as TEXT (it names tokens like <sourceusername>,
       * which the HTML tooltip path would swallow as a tag). A field without them is drawn exactly as before (3 rows).
       * 723 also gets the live preview of one post's caption (components/RepostCaptionPreview.js).
       */
      const help = meta.help || (v1 && typeof v1.help === 'string' ? v1.help : '');
      const placeholder = meta.placeholder || (v1 && typeof v1.placeholder === 'string' && v1.placeholder) || undefined;
      const preview = fieldName === REPOST_CAPTION_FIELD ? <RepostCaptionPreview template={value || ''} /> : null;
      // r16m: the live #1217 hint, under the box (it never changes the box's size)
      const batchHint = fieldName === batch1217Field && batch1217Warn ? (
        <div role="status" data-testid="batch-1217-hint" data-severity={batch1217Warn.severity}
          className={`rounded-xl border p-3 text-sm ${batch1217Warn.severity === 'error' ? 'border-destructive/40 bg-destructive/10' : 'border-warning/40 bg-warning/10'}`}>
          <p className={`flex items-center gap-2 font-semibold ${batch1217Warn.severity === 'error' ? 'text-destructive' : 'text-warning'}`}>
            <Info className="h-4 w-4 shrink-0" aria-hidden />
            {batch1217Warn.title}
          </p>
          <ul className="mt-1.5 list-disc space-y-1 pl-5 text-foreground">
            {batch1217Warn.items.map((it) => <li key={it.code} data-testid={`batch-1217-${it.code}`}>{it.text}</li>)}
          </ul>
          <p className="mt-1.5 text-xs text-muted-foreground">
            On the new worker the batch&apos;s ledger keeps two members from engaging the same person, whatever this list says; this
            list decides which phones&apos; files are read when the ledger is not in use (an older worker, or the server&apos;s ledger
            switched off). Membership itself is on the Follow-back page (Join / Detach).
            {batch1217Warn.batch && (
              <>
                {' '}
                <RouterLink to={`/follow-back?batch=${encodeURIComponent(batch1217Warn.batch)}#fb-batch-pending`} data-testid="batch-1217-open-batch"
                  className="font-medium text-primary hover:underline">
                  Open Batch {batch1217Warn.batch} on the Follow-back page
                </RouterLink>
              </>
            )}
          </p>
        </div>
      ) : null;
      const lineCount = String(value || placeholder || '').split('\n').length;
      return (
        <div key={fieldName} className="space-y-2 col-span-full">
          <Label htmlFor={fieldName}>{fieldLabel}</Label>
          <Textarea
            id={fieldName}
            value={value || ''}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            className="bg-secondary/60 border-border font-mono text-sm"
            rows={help || preview ? Math.min(12, Math.max(5, lineCount + 1)) : 3}
            placeholder={placeholder}
            data-testid={`setting-${fieldName}`}
          />
          {(help || preview) && (
            <div className={preview && help ? 'grid gap-3 lg:grid-cols-2' : ''}>
              {help && (
                <p className="whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-muted-foreground" data-testid={`setting-help-${fieldName}`}>
                  {help}
                </p>
              )}
              {preview}
            </div>
          )}
          {batchHint}
        </div>
      );
    } else if (ctlNumber) {
      return (
        <div key={fieldName} className="flex items-center justify-between p-3 rounded-xl bg-secondary/60 hover:bg-secondary/80 transition-colors gap-4">
          <Label htmlFor={fieldName} className="cursor-pointer flex-1 min-w-0">
            {fieldLabel}
          </Label>
          <Input
            id={fieldName}
            type="number"
            value={value ?? ''}
            onChange={(e) => handleInputChange(fieldName, parseFloat(e.target.value) || 0)}
            className="bg-background border-border text-sm w-24 text-right"
            data-testid={`setting-${fieldName}`}
          />
        </div>
      );
    } else {
      return (
        <div key={fieldName} className="space-y-2">
          <Label htmlFor={fieldName}>{fieldLabel}</Label>
          <Input
            id={fieldName}
            type="text"
            value={value ?? ''}
            onChange={(e) => handleInputChange(fieldName, e.target.value)}
            className="bg-secondary/60 border-border text-sm"
            data-testid={`setting-${fieldName}`}
          />
        </div>
      );
    }
  };

  /*
   * Everything renderField draws a section's controls from, apart from FIELD_METADATA: V1's
   * metadata, the device list, and each field's value plus its Max partner's. KEEP IN STEP with
   * renderField - if it starts reading anything else, add it here, or the All and Accordion views
   * (the users of MemoSectionFields) would keep showing the old control.
   */
  const sectionFieldInputs = (fields) => {
    const inputs = [metaById, devicesData, platform, routeLocked, routeSupersedable, routeActionPending, phoneTransition?.requestedShared];	// route progress also changes the checkbox (and its change handler)
    // r16m: the #1217 hint reads #1216 and #7 (another section), so the section holding #1217 also keys on the hint itself
    if (fields.some((f) => f.name === batch1217Field)) inputs.push(batch1217Warn);
    // r16n: the section holding #1109 also keys on its hint (built from #1109 and #1110, which the loop below adds as well)
    if (fields.some((f) => f.name === storyCfMinField)) inputs.push(storyCfWarn);
    // r16p: the section holding #776 keys on its "Seen" hint (and on the field name, found once the metadata registered it)
    if (dmOpenField && fields.some((f) => f.name === dmOpenField)) inputs.push(dmOpenField, dmSeenHint);
    fields.forEach((f) => {
      inputs.push(f.name, settings[f.name]);
      const partner = maxPartnerOf(f.name);
      if (partner) inputs.push(settings[partner]);
    });
    return inputs;
  };

  const renderSectionContent = (sectionName, fields) => {
    if (!fields || fields.length === 0) return null;
    
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {fields.map(field => renderField(field.name, settings[field.name]))}
      </div>
    );
  };

  // Shorten section name for sidebar display
  const getShortenedSectionName = (sectionName) => {
    return sectionName
      .replace(' Settings', '')
      .replace(' (Hijacks Comments Module)', '')
      .replace(' (Follow Module Extension)', '')
      .replace('/Post Scheduling', '')
      .replace(' Limits', '');
  };

  /*
   * Accordion View with Fixed Sidebar Navigation.
   *
   * Two modes (the checkbox at the top; see ACCORDION_SINGLE_KEY):
   *   all open (default) - every section starts open, several can be open at once, each header
   *                        closes its own; the sidebar/chips jump to a section (opening it if it
   *                        was closed) and follow the scroll; Expand all / Collapse all.
   *   one at a time      - exactly the behaviour before 2026-09-29: one open section, opening
   *                        another closes it, the sidebar opens + scrolls.
   * Both draw each section through AccordionSection and its controls through MemoSectionFields
   * (typing speed with ~630 controls open - see their notes), from the same filteredFieldsBySection
   * and renderField as every view.
   */
  const renderAccordionView = () => {
    const sections = visibleSections;
    const single = accordionSingle;
    const isActiveSection = (s) => (single
      ? activeSidebarSection === s || openAccordionSection === s
      : activeSidebarSection === s);
    // Where a jump lands: below the top bar, and on phones (all-open mode) below the sticky chips.
    const anchorOffset = single
      ? 'scroll-mt-[calc(var(--topbar-h)+1rem)]'
      : 'scroll-mt-[calc(var(--topbar-h)+var(--safe-top)+4rem)] lg:scroll-mt-[calc(var(--topbar-h)+1rem)]';
    const accordionProps = single
      ? {
        type: 'single',
        collapsible: true,
        value: openAccordionSection,
        onValueChange: (value) => {
          setOpenAccordionSection(value);
          setActiveSidebarSection(value);
        },
      }
      : { type: 'multiple', value: openSections, onValueChange: handleAccordionMultiChange };

    return (
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start" data-testid="accordion-view">
        {/* Section chips (< lg): the sticky sidebar below needs a tall viewport, so phones and
            tablets get a horizontal strip that scrolls - same click handler, its own test ids. With
            every section open the page is long, so there the strip stays pinned under the top bar
            and follows the scroll like the sidebar does. */}
        <div
          ref={chipStripRef}
          className={`lg:hidden flex gap-1.5 overflow-x-auto no-scrollbar ${
            single
              ? 'pb-1'
              : 'sticky top-[calc(var(--topbar-h)+var(--safe-top))] z-20 -mx-4 px-4 py-2 sm:-mx-6 sm:px-6 glass border-b border-border/70'
          }`}
          aria-label="Sections"
          data-testid="accordion-chip-strip"
        >
          {sections.map(sectionName => {
            const fields = filteredFieldsBySection[sectionName];
            const isActive = isActiveSection(sectionName);

            return (
              <button
                key={sectionName}
                ref={(el) => { chipRefs.current[sectionName] = el; }}
                type="button"
                onClick={() => handleSidebarSectionClick(sectionName)}
                aria-current={isActive ? 'true' : undefined}
                className={`tap shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'bg-primary/15 text-primary ring-1 ring-primary/25'
                    : 'bg-secondary/60 text-muted-foreground ring-1 ring-border hover:text-foreground'
                }`}
                data-testid={`section-chip-${sectionSlug(sectionName)}`}
              >
                {getShortenedSectionName(sectionName)}
                <span className="tnum opacity-70">{fields.length}</span>
              </button>
            );
          })}
        </div>

        {/* Left Sidebar - Section Navigation - Constrained to viewport height (lg and up; it sits
            below the shell's sticky top bar) */}
        <div
          className="hidden lg:block w-56 shrink-0 self-start sticky top-[calc(var(--topbar-h)+1rem)]"
          style={{ height: 'calc(100dvh - var(--topbar-h) - 2rem)', maxHeight: 'calc(100dvh - var(--topbar-h) - 2rem)' }}
        >
          <div className="aurora-card h-full flex flex-col overflow-hidden">
            {/* Header - Fixed at top of sidebar, never scrolls away */}
            <div className="px-3 py-2.5 bg-secondary/60 border-b border-border shrink-0">
              <h3 className="font-heading text-sm font-semibold flex items-center gap-2">
                <List className="h-4 w-4 text-primary" />
                Sections
              </h3>
            </div>
            {/* Scrollable Section List - only this part scrolls, within constrained height */}
            <div
              ref={sidebarListRef}
              className="flex-1 overflow-y-auto overscroll-contain"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              <div className="p-2 space-y-1">
                {sections.map(sectionName => {
                  const fields = filteredFieldsBySection[sectionName];
                  const isActive = isActiveSection(sectionName);
                  const shortName = getShortenedSectionName(sectionName);

                  return (
                    <button
                      key={sectionName}
                      ref={(el) => { sidebarItemRefs.current[sectionName] = el; }}
                      onClick={() => handleSidebarSectionClick(sectionName)}
                      aria-current={isActive ? 'true' : undefined}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all duration-200 ${
                        isActive
                          ? 'bg-primary/15 text-primary ring-1 ring-primary/25'
                          : 'hover:bg-secondary/70 text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`sidebar-section-${sectionSlug(sectionName)}`}
                    >
                      <span className="truncate flex-1 text-xs">{shortName}</span>
                      <div className="flex items-center gap-1 shrink-0 ml-1">
                        <span className={`text-[10px] tnum px-1.5 py-0.5 rounded ${isActive ? 'bg-primary/25' : 'bg-secondary'}`}>
                          {fields.length}
                        </span>
                        {isActive && <ChevronRight className="h-3 w-3" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Accordion */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* How the sections open: every one (default) or one at a time. */}
          <div
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-xl bg-secondary/40 px-3 py-2.5 ring-1 ring-border"
            data-testid="accordion-toolbar"
          >
            <div className="flex min-w-0 items-start gap-2.5">
              <Checkbox
                id="accordion-one-at-a-time"
                checked={single}
                onCheckedChange={handleAccordionSingleChange}
                aria-describedby="accordion-one-at-a-time-hint"
                className="mt-0.5"
                data-testid="accordion-single-toggle"
              />
              <div className="min-w-0 leading-tight">
                <Label htmlFor="accordion-one-at-a-time" className="cursor-pointer text-sm font-medium text-foreground">
                  One section at a time
                </Label>
                <p id="accordion-one-at-a-time-hint" className="mt-0.5 text-xs text-muted-foreground">
                  {single
                    ? 'Opening a section closes the others. Untick to keep every section open.'
                    : 'Every section starts open. Tick to show only one at a time.'}
                </p>
              </div>
            </div>
            {!single && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExpandAll}
                  disabled={openSections.length === sections.length}
                  className="tap text-muted-foreground hover:text-foreground"
                  data-testid="accordion-expand-all"
                >
                  <ChevronsUpDown className="h-4 w-4" />
                  Expand all
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCollapseAll}
                  disabled={openSections.length === 0}
                  className="tap text-muted-foreground hover:text-foreground"
                  data-testid="accordion-collapse-all"
                >
                  <ChevronsDownUp className="h-4 w-4" />
                  Collapse all
                </Button>
              </div>
            )}
          </div>

          <Accordion
            key={single ? 'single' : 'multiple'}
            {...accordionProps}
            className="space-y-3"
          >
            {sections.map(sectionName => {
              const fields = filteredFieldsBySection[sectionName];

              return (
                <AccordionSection
                  key={sectionName}
                  sectionName={sectionName}
                  count={fields.length}
                  anchorOffset={anchorOffset}
                  registerRef={registerSectionRef}
                  inputs={sectionFieldInputs(fields)}
                  draw={() => renderSectionContent(sectionName, fields)}
                />
              );
            })}
          </Accordion>
        </div>
      </div>
    );
  };

  // Tabs View - One tab per section
  const renderTabsView = () => (
    <Tabs value={activeTab || orderedSections[0]} onValueChange={setActiveTab} className="space-y-4">
      <div className="overflow-x-auto pb-2">
        <TabsList className="inline-flex h-auto p-1 bg-secondary/60 gap-1 flex-wrap">
          {orderedSections.map(sectionName => {
            const fields = filteredFieldsBySection[sectionName];
            if (!fields || fields.length === 0) return null;
            
            const shortName = getShortenedSectionName(sectionName);
            
            return (
              <TabsTrigger 
                key={sectionName} 
                value={sectionName}
                className="text-xs px-3 py-2 whitespace-nowrap data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {shortName}
                <span className="ml-1 text-[10px] opacity-70">({fields.length})</span>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </div>
      
      {orderedSections.map(sectionName => {
        const fields = filteredFieldsBySection[sectionName];
        if (!fields || fields.length === 0) return null;
        
        return (
          <TabsContent key={sectionName} value={sectionName}>
            <Card className="aurora-card">
              <CardHeader>
                <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
                  <Settings2 className="h-5 w-5 text-primary" />
                  {sectionName}
                </CardTitle>
                <CardDescription>
                  {fields.length} setting{fields.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {renderSectionContent(sectionName, fields)}
              </CardContent>
            </Card>
          </TabsContent>
        );
      })}
    </Tabs>
  );

  // Nested Tabs View - Main categories with sub-tabs
  const renderNestedTabsView = () => {
    const availableCategories = Object.entries(MAIN_CATEGORIES).filter(([_, sections]) =>
      sections.some(s => filteredFieldsBySection[s]?.length > 0)
    );

    return (
      <div className="space-y-4">
        {/* Main Category Tabs */}
        <Tabs value={activeCategory} onValueChange={setActiveCategory}>
          <TabsList className="w-full justify-start h-auto p-1 bg-secondary/60 gap-1 flex-wrap">
            {availableCategories.map(([category]) => (
              <TabsTrigger 
                key={category}
                value={category}
                className="px-4 py-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
              >
                {category}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Sub-section content */}
        {activeCategory && MAIN_CATEGORIES[activeCategory] && (
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
            {/* Sidebar for sub-sections - a horizontal chip strip below lg, a column from lg */}
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-1 lg:w-64 lg:shrink-0 lg:flex-col lg:gap-1 lg:overflow-visible lg:pb-0">
              {MAIN_CATEGORIES[activeCategory].map(section => {
                const fields = filteredFieldsBySection[section];
                if (!fields || fields.length === 0) return null;

                const isActive = activeSubTab === section;
                const shortName = getShortenedSectionName(section);

                return (
                  <button
                    key={section}
                    type="button"
                    onClick={() => setActiveSubTab(section)}
                    className={`tap shrink-0 text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 whitespace-nowrap transition-colors lg:w-full lg:whitespace-normal ${
                      isActive
                        ? 'bg-primary/15 text-primary ring-1 ring-primary/25'
                        : 'bg-secondary/60 text-muted-foreground ring-1 ring-border hover:text-foreground lg:bg-transparent lg:ring-0 lg:hover:bg-secondary/70'
                    }`}
                  >
                    <span className="truncate">{shortName}</span>
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-xs opacity-70 tnum">({fields.length})</span>
                      {isActive && <ChevronRight className="h-4 w-4" />}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Content area */}
            <div className="flex-1 min-w-0">
              {activeSubTab && filteredFieldsBySection[activeSubTab] && (
                <Card className="aurora-card">
                  <CardHeader>
                    <CardTitle className="font-heading text-base font-semibold flex items-center gap-2">
                      <Settings2 className="h-5 w-5 text-primary" />
                      {activeSubTab}
                    </CardTitle>
                    <CardDescription>
                      {filteredFieldsBySection[activeSubTab].length} setting{filteredFieldsBySection[activeSubTab].length !== 1 ? 's' : ''}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {renderSectionContent(activeSubTab, filteredFieldsBySection[activeSubTab])}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  /*
   * All View - V1's single long page (owner, 2026-09-28: "all settings in 1 page ... without
   * needing to toggle anything on or off"). Every section is drawn open, one after another, in
   * the order the other views use.
   *
   * It is a LAYOUT only. The fields come from the same filteredFieldsBySection and are drawn by
   * the same renderSectionContent/renderField as every other view, so search (#number too), V1's
   * plan gates, the server-defined settings, dirty tracking and Save cannot behave differently
   * here. MemoSectionFields only skips redrawing a section whose inputs did not change (typing lag
   * with ~630 controls - see its note). The chip strip is a shortcut, never a gate: nothing on the
   * page needs it to be seen.
   */
  const renderAllView = () => {
    const sections = orderedSections.filter((s) => filteredFieldsBySection[s]?.length > 0);
    const anchorOffset = 'scroll-mt-[calc(var(--topbar-h)+var(--safe-top)+1rem)]';

    return (
      <div className="space-y-4" data-testid="all-settings-view">
        {/* Jump strip: one scrolling row on phones and tablets, wrapped rows from lg. */}
        <nav
          ref={allJumpStripRef}
          aria-label="Jump to section"
          className={`flex gap-1.5 overflow-x-auto no-scrollbar pb-1 lg:flex-wrap lg:overflow-visible ${anchorOffset}`}
          data-testid="all-jump-strip"
        >
          {sections.map((sectionName) => (
            <button
              key={sectionName}
              type="button"
              onClick={() => handleJumpToSection(sectionName)}
              className="tap shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium whitespace-nowrap transition-colors bg-secondary/60 text-muted-foreground ring-1 ring-border hover:text-foreground"
              data-testid={`jump-chip-${sectionSlug(sectionName)}`}
            >
              {getShortenedSectionName(sectionName)}
              <span className="tnum opacity-70">{filteredFieldsBySection[sectionName].length}</span>
            </button>
          ))}
        </nav>

        {sections.map((sectionName) => {
          const fields = filteredFieldsBySection[sectionName];
          const slug = sectionSlug(sectionName);
          return (
            <Card
              key={sectionName}
              ref={(el) => { allSectionRefs.current[sectionName] = el; }}
              role="region"
              aria-labelledby={`all-heading-${slug}`}
              className={`aurora-card ${anchorOffset}`}
              data-testid={`all-section-${slug}`}
            >
              <CardHeader className="flex-row items-start justify-between gap-3 space-y-0 p-4 sm:p-6">
                <div className="min-w-0 space-y-1">
                  <h2 id={`all-heading-${slug}`} className="font-heading text-base font-semibold leading-tight flex items-center gap-2">
                    <Settings2 className="h-5 w-5 shrink-0 text-primary" />
                    <span className="min-w-0 break-words">{sectionName}</span>
                  </h2>
                  <CardDescription className="tnum">
                    {fields.length} setting{fields.length !== 1 ? 's' : ''}
                  </CardDescription>
                </div>
                <button
                  type="button"
                  onClick={handleBackToSectionList}
                  className="tap shrink-0 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-muted-foreground hover:bg-secondary/70 hover:text-foreground"
                  title="Back to the section list"
                  aria-label="Back to the section list"
                >
                  <ArrowUp className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Sections</span>
                </button>
              </CardHeader>
              <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
                <MemoSectionFields
                  inputs={sectionFieldInputs(fields)}
                  draw={() => renderSectionContent(sectionName, fields)}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <SettingsSkeleton />
    );
  }

  // Wait for settings to be initialized (either from API or generated defaults)
  if (!settings) {
    return (
      <SettingsSkeleton />
    );
  }

  const visibleFieldCount = Object.values(filteredFieldsBySection).reduce((sum, fields) => sum + fields.length, 0);

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <PageHeader
        icon={Settings2}
        title={slotData?.slot_label || slotName}
        description={`${visibleFieldCount} settings • ${orderedSections.length} sections`}
        back={{ to: '/slots', label: 'Slots' }}
        actions={(
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/slots')}
              className="tap"
              data-testid="back-to-slots-btn"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            {hasChanges && (
              <span className="inline-flex items-center gap-2 rounded-full bg-warning/10 px-3 py-1.5 text-xs font-medium text-warning ring-1 ring-warning/25">
                <span className="h-1.5 w-1.5 rounded-full bg-warning animate-pulse" aria-hidden />
                Unsaved changes
              </span>
            )}
            {!writesEnabled && (writeInfo || writesUnavailable) && (
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-3 py-1.5 text-xs font-medium text-warning ring-1 ring-warning/25"
                title={writesOffReason}
                data-testid="writes-disabled-banner"
              >
                <ShieldOff className="h-4 w-4" />
                {writesUnavailable ? 'Saving is unavailable on this server' : 'Saving is disabled by the admin'}
              </span>
            )}
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isPending || !writesEnabled}
              title={!writesEnabled ? writesOffReason : (!hasChanges ? 'Nothing has changed yet' : undefined)}
              className="tap shadow-glow"
              data-testid="save-settings-btn"
            >
              {updateMutation.isPending ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Save All Settings
            </Button>
          
            {/* Template + History buttons */}
            <Button
              variant="outline"
              onClick={() => setSavePresetDialogOpen(true)}
              disabled={!writesEnabled}
              className="tap border-success/30 text-success hover:bg-success/10 hover:text-success"
              data-testid="save-preset-btn"
            >
              <BookmarkPlus className="h-4 w-4" />
              Save as Template
            </Button>
          
            <Button
              variant="outline"
              onClick={() => setLoadPresetDialogOpen(true)}
              disabled={!writesEnabled}
              className="tap border-warning/30 text-warning hover:bg-warning/10 hover:text-warning"
              data-testid="load-preset-btn"
            >
              <BookOpen className="h-4 w-4" />
              Use Template
            </Button>

            <Button
              variant="outline"
              onClick={() => setHistoryDialogOpen(true)}
              disabled={!writesEnabled}
              className="tap border-accent/30 text-accent hover:bg-accent/10 hover:text-accent"
              data-testid="history-btn"
            >
              <History className="h-4 w-4" />
              History
            </Button>
            {DOC_DRAWER && (
              <Button
                variant="outline"
                onClick={() => setDoctorOpen(true)}
                className="tap border-primary/30 text-primary hover:bg-primary/10 hover:text-primary"
                data-testid="check-slot-btn"
              >
                <Stethoscope className="h-4 w-4" />
                Check this slot
                {doctorPanel && doctorPanel.findings.length > 0 && (
                  <span className="tnum rounded-full bg-warning/20 px-1.5 text-[11px] text-warning" data-testid="check-slot-count">{doctorPanel.findings.length}</span>
                )}
              </Button>
            )}
          </>
        )}
      />

      {/* r16sd: this slot's holds (the server's words, red first), each with its fix. Review SF4: a "Switch the phone on" fix acts on
          the STORED phone (the baseline the server's hold names), never on an unsaved choice in the #7 dropdown. */}
      {DOC_HEADER && doctorPanel && doctorPanel.doctor && doctorPanel.banners.length > 0 && (
        <SlotBanner holds={doctorPanel.banners} variant="page" slotName={slotName} deviceId={doctorDeviceId}
          onFocusSetting={focusSetting} onFixed={() => { refreshPanel().catch(() => {}); }} testid="settings-doctor-header"
          applyBlocked={hasChanges} />
      )}

      {/* r16sd: what the last save found (WARN never blocks; the save went through) */}
      {DOC_SAVE && saveCheck && (
        <SaveFindingsCard findings={saveCheck.findings} slotName={slotName} deviceId={doctorDeviceId}
          onFocusSetting={focusSetting} onClose={() => setSaveCheck(null)} applyBlocked={hasChanges}
          onResolved={(rule, how) => {
            console.info(`${DOCTOR_TAG} save ${slotName}: ${rule} ${how} from the save card`);
            setSaveCheck((prev) => {
              const left = prev ? prev.findings.filter((x) => x.rule !== rule) : [];
              return left.length ? { ...prev, findings: left } : null;
            });
            refreshPanel().catch(() => {});
          }} />
      )}

      {DOC_DRAWER && (
        <DoctorDrawer slotName={slotName} open={doctorOpen} onOpenChange={setDoctorOpen} deviceId={doctorDeviceId} applyBlocked={hasChanges}
          onFocusSetting={focusSetting} />
      )}

      {/* R16H U1 (2026-10-12): 601 on, 613 empty, no other repost source - a warning card (never a block), full width under the
          header so the header keeps its layout; the save toast repeats it. */}
      {blankSourceWarn && (
        <Card className="aurora-card border-warning/40 bg-warning/10" role="status" data-testid="blank-source-banner">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="h-10 w-10 shrink-0 rounded-xl bg-warning/20 flex items-center justify-center">
                <Info className="h-5 w-5 text-warning" />
              </div>
              <div className="min-w-0 space-y-1">
                <h3 className="font-semibold text-warning">{blankSourceWarn.title}</h3>
                <p className="text-sm text-foreground">{blankSourceWarn.text}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/*
        r16 (2026-10-03): a slot that is not Instagram says so, and says why settings are missing. The hiding itself is
        the server's (platform gates + the slot's flags); the "TikTok Settings" section is where the TikTok-only
        settings will come in, and is drawn once the server defines one. Nothing is drawn on an Instagram slot.
      */}
      {platform !== 'INSTAGRAM' && (
        <Card className="aurora-card" data-testid="tiktok-platform-note">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className="h-10 w-10 shrink-0 rounded-xl bg-primary/15 flex items-center justify-center">
                <Info className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 space-y-1">
                <h3 className="font-semibold text-foreground">
                  {platform === 'TIKTOK' ? 'TikTok slot' : `Platform "${platform}" is not one this server knows`}
                </h3>
                {platform === 'TIKTOK' ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Instagram-only settings are hidden here. Settings of TikTok features that are not built yet are
                      hidden too, so nothing is set that no worker obeys.
                    </p>
                    <p className="text-sm text-muted-foreground" data-testid="tiktok-ready-modules">
                      {(() => {
                        const ready = readyTikTokModules(slotData && slotData.gates);
                        return ready.length
                          ? `TikTok features with settings now: ${ready.join(', ')}.`
                          : 'No TikTok feature has settings yet.';
                      })()}
                    </p>
                    <p className="text-sm text-muted-foreground" data-testid="tiktok-settings-placeholder">
                      TikTok Settings: TikTok-only settings will appear in their own section as the TikTok worker gains them.
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No worker runs this slot until its Platform (setting 15) is set to {platformName('INSTAGRAM')} or {platformName('TIKTOK')}.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Automation Persona Section - Only visible if site owner enabled random persona for this user */}
      {enableRandomPersona && (
        <Card className="aurora-card bg-gradient-to-r from-warning/10 to-accent/10">
          <CardContent className="p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-warning/20 flex items-center justify-center">
                  <Settings2 className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Automation Persona</h3>
                  <p className="text-sm text-muted-foreground">Controls the speed and style of automation actions</p>
                </div>
              </div>
              <Select
                value={settings?.automation_persona || 'normal'}
                onValueChange={(value) => {
                  setSettings(prev => ({ ...prev, automation_persona: value }));
                  setHasChanges(true);
                }}
              >
                <SelectTrigger className="w-full sm:w-40 bg-secondary/60 border-warning/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fast">
                    <div className="flex items-center gap-2">
                      <span className="text-success">⚡</span> Fast
                    </div>
                  </SelectItem>
                  <SelectItem value="normal">
                    <div className="flex items-center gap-2">
                      <span className="text-primary">●</span> Normal
                    </div>
                  </SelectItem>
                  <SelectItem value="slow">
                    <div className="flex items-center gap-2">
                      <span className="text-warning">🐢</span> Slow
                    </div>
                  </SelectItem>
                  <SelectItem value="careful">
                    <div className="flex items-center gap-2">
                      <span className="text-accent">🎯</span> Careful
                    </div>
                  </SelectItem>
                  <SelectItem value="careless">
                    <div className="flex items-center gap-2">
                      <span className="text-danger">🎲</span> Careless
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {settings?.automation_persona === 'fast' && 'Quick actions with minimal delays - for speed-focused operations'}
              {settings?.automation_persona === 'normal' && 'Balanced timing - recommended for most use cases'}
              {settings?.automation_persona === 'slow' && 'Careful, human-like pacing - lower detection risk'}
              {settings?.automation_persona === 'careful' && 'Extra cautious with longer delays - safest option'}
              {settings?.automation_persona === 'careless' && 'Variable, unpredictable timing - mimics casual browsing'}
              {!settings?.automation_persona && 'Balanced timing - recommended for most use cases'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* View Mode Selector & Search */}
      <Card className="aurora-card">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
            {/* View Mode Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground whitespace-nowrap">View:</span>
              <div className="flex bg-secondary/60 rounded-xl p-1 gap-1">
                <button
                  onClick={() => handleViewModeChange(VIEW_MODES.ALL)}
                  className={`tap p-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                    viewMode === VIEW_MODES.ALL
                      ? 'bg-primary text-primary-foreground shadow-glow'
                      : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
                  }`}
                  title="All settings on one page"
                  aria-label="All settings on one page"
                  data-testid="view-mode-all"
                >
                  <ScrollText className="h-4 w-4" />
                  <span className="hidden sm:inline">All</span>
                </button>
                <button
                  onClick={() => handleViewModeChange(VIEW_MODES.ACCORDION)}
                  className={`tap p-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                    viewMode === VIEW_MODES.ACCORDION 
                      ? 'bg-primary text-primary-foreground shadow-glow'
                      : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
                  }`}
                  title="Accordion View"
                  aria-label="Accordion view"
                  data-testid="view-mode-accordion"
                >
                  <List className="h-4 w-4" />
                  <span className="hidden sm:inline">Accordion</span>
                </button>
                <button
                  onClick={() => handleViewModeChange(VIEW_MODES.TABS)}
                  className={`tap p-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                    viewMode === VIEW_MODES.TABS 
                      ? 'bg-primary text-primary-foreground shadow-glow'
                      : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
                  }`}
                  title="Tabs View"
                  aria-label="Tabs view"
                  data-testid="view-mode-tabs"
                >
                  <LayoutGrid className="h-4 w-4" />
                  <span className="hidden sm:inline">Tabs</span>
                </button>
                <button
                  onClick={() => handleViewModeChange(VIEW_MODES.NESTED)}
                  className={`tap p-2 rounded-lg transition-colors flex items-center gap-2 text-sm ${
                    viewMode === VIEW_MODES.NESTED 
                      ? 'bg-primary text-primary-foreground shadow-glow'
                      : 'text-muted-foreground hover:bg-secondary/70 hover:text-foreground'
                  }`}
                  title="Nested Categories View"
                  aria-label="Nested categories view"
                  data-testid="view-mode-nested"
                >
                  <Layers className="h-4 w-4" />
                  <span className="hidden sm:inline">Nested</span>
                </button>
              </div>
            </div>

            {/* Search Bar */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by setting name, number, or label..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  // All-open accordion: a search opens every section it matches, even ones the
                  // user had closed - a match inside a closed section would look like no match.
                  setClosedSections((prev) => (prev.size ? new Set() : prev));
                }}
                className="pl-10 bg-secondary/60 border-border"
                data-testid="search-settings"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-2">
            <Info className="h-3 w-3 text-accent" />
            {/* The note that used to sit here announced that saving also rewrote #6 Enable Locking
                and #4 App Activity behind your back. Both rewrites are gone - they were V2
                inventions that contradicted V1 and moved real data - so saving now changes exactly
                what you changed, and nothing else. See applyAutoSettings for the measurements. */}
            Hover over pink info icons for detailed explanations • Use #number to search by setting number
          </p>
        </CardContent>
      </Card>

      {/* Settings Content - Render based on view mode */}
      <PhoneDriverTransitionNotice
        transition={phoneTransition}
        onRetry={() => runRouteAction('retry', () => retryTransition.mutate(phoneTransition))}
        onCancel={() => runRouteAction('cancel', () => cancelTransition.mutate(phoneTransition))}
        onSaveRoute={(shared) => { if (!updateMutation.isPending) runRouteAction('route-save', () => routeSave.mutate(shared)); }}
        canSaveRoute={writesEnabled}
        retrying={retryTransition.isPending}
        cancelling={cancelTransition.isPending}
        savingRoute={routeSave.isPending}
        busy={routeActionPending || updateMutation.isPending}
      />
      <div className="space-y-4" data-testid="settings-container">
        {viewMode === VIEW_MODES.ALL && renderAllView()}
        {viewMode === VIEW_MODES.ACCORDION && renderAccordionView()}
        {viewMode === VIEW_MODES.TABS && renderTabsView()}
        {viewMode === VIEW_MODES.NESTED && renderNestedTabsView()}
      </div>

      {/* Sticky Save Button - sits above the shell's bottom tab bar (< lg) and the home indicator.
          It saves EXACTLY what the header button saves, so it must be gated exactly the same way.
          It was not: it missed `!writesEnabled` entirely, and its `isLoading` is a react-query v4
          name that v5 does not define on a mutation - so `disabled` was literally `undefined` and
          the button was always live. On a server without the settings-write endpoint that gave the
          customer a greyed-out Save at the top and a working-looking one at the bottom that could
          only ever fail. One gate, one behaviour, and it says why on hover. */}
      {/* z-50, not z-40: the mobile tab bar and the desktop sidebar are both z-40, and equal
          z-index falls back to DOM order - which the shell wins, because it renders after the
          page. It sits above the bar by the calc below, but there is no reason to leave that
          resting on paint order. The assistant bubble keeps clear of it via lib/fabStack. */}
      {hasChanges && (
        <div className="fixed right-4 z-50 bottom-[calc(var(--bottombar-h)+var(--safe-bottom)+1rem)] lg:bottom-6 lg:right-6">
          <Button
            onClick={handleSave}
            disabled={!writesEnabled || updateMutation.isPending}
            title={!writesEnabled ? writesOffReason : undefined}
            size="lg"
            className="tap rounded-full shadow-glow"
            data-testid="floating-save-btn"
          >
            {updateMutation.isPending ? (
              <RefreshCw className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            Save Changes
          </Button>
        </div>
      )}
      
      {/* Save as Preset Dialog */}
      <Dialog open={savePresetDialogOpen} onOpenChange={setSavePresetDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-auto sm:max-w-lg max-h-[90vh] overflow-y-auto border-border rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Save Settings as Template</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Save the current settings as a reusable template. Identity fields (IG username/password, device, label) are never included.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="preset-name" className="text-foreground/90">Preset Name *</Label>
              <Input
                id="preset-name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                placeholder="e.g., Aggressive Growth, Safe Mode"
                className="bg-secondary/60 border-border text-foreground placeholder:text-muted-foreground/70"
                data-testid="preset-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="preset-description" className="text-foreground/90">Description (optional)</Label>
              <Textarea
                id="preset-description"
                value={presetDescription}
                onChange={(e) => setPresetDescription(e.target.value)}
                placeholder="Describe what this preset is for..."
                className="bg-secondary/60 border-border text-foreground placeholder:text-muted-foreground/70"
                rows={3}
                data-testid="preset-description-input"
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setSavePresetDialogOpen(false)}
              className="tap border-border text-foreground/90"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveAsPreset}
              className="tap bg-success text-primary-foreground hover:bg-success/90"
              data-testid="confirm-save-preset-btn"
            >
              <BookmarkPlus className="h-4 w-4" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* History (undo) Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-auto sm:max-w-2xl max-h-[90vh] overflow-y-auto border-border rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Settings history - {slotName}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              The last {historyData?.retention ?? 20} changes to this slot. Revert applies a change's
              previous values back - and is itself recorded, so you can redo.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 max-h-96 overflow-y-auto space-y-2">
            {(historyData?.changes || []).length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">No recorded changes yet.</p>
            )}
            {(historyData?.changes || []).map((c) => (
              <div key={c.changeId} className="p-3 rounded-xl border border-border bg-secondary/60" data-testid={`history-${c.changeId}`}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-sm">
                      <span className="text-foreground font-medium tnum">{c.count} setting{c.count === 1 ? '' : 's'}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-secondary text-foreground/90">{c.source}</span>
                      <span className="text-xs text-muted-foreground tnum">{c.at ? new Date(c.at).toLocaleString() : ''}</span>
                    </div>
                    {c.reason && <div className="text-xs text-muted-foreground mt-0.5 truncate">{c.reason}</div>}
                    <div className="text-xs text-muted-foreground/80 mt-1 space-y-0.5">
                      {(c.values || []).slice(0, 4).map(v => (
                        <div key={v.id} className="truncate">
                          <span className="text-muted-foreground">{v.label || v.field}</span>
                          {': '}<span className="text-danger/80 line-through">{v.old === null ? 'empty' : String(v.old).slice(0, 40)}</span>
                          {' -> '}<span className="text-success/90">{v.new === null ? 'empty' : String(v.new).slice(0, 40)}</span>
                        </div>
                      ))}
                      {(c.values || []).length > 4 && <div>...and {c.values.length - 4} more</div>}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={revertMutation.isPending}
                    onClick={() => {
                      if (confirmDiscardingEdits(`Revert these ${c.count} setting(s) to their previous values?`)) {
                        revertMutation.mutate(c.changeId);
                      }
                    }}
                    className="tap border-accent/40 text-accent hover:bg-accent/10 hover:text-accent shrink-0 self-start sm:self-auto"
                    data-testid={`revert-${c.changeId}`}
                  >
                    <Undo2 className="h-3.5 w-3.5 mr-1" /> Revert
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Load Template Dialog */}
      <Dialog open={loadPresetDialogOpen} onOpenChange={setLoadPresetDialogOpen}>
        <DialogContent className="w-[calc(100vw-2rem)] sm:w-auto sm:max-w-lg max-h-[90vh] overflow-y-auto border-border rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Apply a Template</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Templates never carry identity fields (IG username/password, device, label). Applying
              shows a preview first and is undoable from History.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {presets && presets.length > 0 ? (
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {presets.map((preset) => (
                  <div
                    key={preset.id}
                    className={`p-3 rounded-xl border cursor-pointer transition-colors ${
                      selectedPresetId === preset.id
                        ? 'border-warning bg-warning/10'
                        : 'border-border hover:border-primary/40 hover:bg-secondary/60'
                    }`}
                    onClick={() => setSelectedPresetId(preset.id)}
                    data-testid={`preset-item-${preset.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-foreground truncate">{preset.name}</h4>
                        {preset.description && (
                          <p className="text-sm text-muted-foreground mt-1">{preset.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          Created: {new Date(preset.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeletePreset(preset.id, preset.name);
                        }}
                        className="tap shrink-0 text-danger hover:text-danger hover:bg-danger/10"
                        aria-label={`Delete template ${preset.name}`}
                        data-testid={`delete-preset-${preset.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="aurora-card aurora-card-dashed text-center px-4 py-8 text-muted-foreground">
                <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No templates saved yet</p>
                <p className="text-sm mt-1">Save your current settings as a template first</p>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setLoadPresetDialogOpen(false);
                setSelectedPresetId('');
              }}
              className="tap border-border text-foreground/90"
            >
              Cancel
            </Button>
            <Button
              onClick={handleLoadPreset}
              disabled={!selectedPresetId}
              className="tap bg-warning text-primary-foreground hover:bg-warning/90 disabled:opacity-50"
              data-testid="confirm-load-preset-btn"
            >
              <BookOpen className="h-4 w-4" />
              Apply Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
